import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as fabric from 'fabric';
import '../css/reception.css';
import supabase from '../supabaseClient';

const randomUUID = () =>
  crypto.randomUUID?.() ??
  '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, c =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );

const RELATIONSHIP_OPTIONS = [
  { value: '', label: '— None —' },
  // Couple
  { value: 'groom',                    label: 'Groom — 新郎 / 老公' },
  { value: 'bride',                    label: 'Bride — 新娘 / 新抱' },
  // Parents-in-law
  { value: 'father_in_law',            label: "Father-in-law — 老爺 / 家公" },
  { value: 'mother_in_law',            label: "Mother-in-law — 奶奶 / 家婆" },
  // Paternal grandparents
  { value: 'paternal_grandfather',     label: "Paternal Grandfather — 爺爺 / 阿爺" },
  { value: 'paternal_grandmother',     label: "Paternal Grandmother — 嫲嫲 / 阿嫲" },
  // Great-grandparent generation (paternal)
  { value: 'grandfathers_older_bro',   label: "Grandfather's Older Brother — 伯公" },
  { value: 'grandfathers_younger_bro', label: "Grandfather's Younger Brother — 叔公" },
  { value: 'grandfathers_sister',      label: "Grandfather's Sister — 姑婆" },
  { value: 'grandmothers_brother',     label: "Grandmother's Brother — 舅公" },
  { value: 'grandmothers_sister',      label: "Grandmother's Sister — 姨婆" },
  // Dad's siblings & spouses
  { value: 'dads_older_brother',       label: "Dad's Older Brother — 伯父 / 阿伯" },
  { value: 'dads_older_brothers_wife', label: "Dad's Older Brother's Wife — 伯娘" },
  { value: 'dads_younger_brother',     label: "Dad's Younger Brother — 叔叔 / 阿叔" },
  { value: 'dads_younger_brothers_wife', label: "Dad's Younger Brother's Wife — 阿嬸" },
  { value: 'dads_older_sister',        label: "Dad's Sister (Older) — 姑媽" },
  { value: 'dads_younger_sister',      label: "Dad's Sister (Younger) — 姑姐" },
  { value: 'paternal_aunts_husband',   label: "Paternal Aunt's Husband — 姑丈" },
  // Husband's siblings
  { value: 'husbands_elder_brother',   label: "Husband's Elder Brother — 大伯" },
  { value: 'husbands_younger_brother', label: "Husband's Younger Brother — 小叔 / 叔仔" },
  { value: 'husbands_elder_sister',    label: "Husband's Elder Sister — 姑奶" },
  { value: 'husbands_younger_sister',  label: "Husband's Younger Sister — 姑仔" },
  // Siblings-in-law (own siblings' spouses)
  { value: 'older_brothers_wife',      label: "Older Brother's Wife — 阿嫂 / 嫂嫂" },
  { value: 'younger_brothers_wife',    label: "Younger Brother's Wife — 弟婦" },
  { value: 'older_sisters_husband',    label: "Older Sister's Husband — 姐夫" },
  { value: 'younger_sisters_husband',  label: "Younger Sister's Husband — 妹夫" },
  // Nephews & nieces
  { value: 'brothers_son',             label: "Brother's Son (Nephew) — 侄 / 侄仔" },
  { value: 'brothers_daughter',        label: "Brother's Daughter (Niece) — 侄女" },
  { value: 'sisters_son',              label: "Sister's Son (Nephew) — 外甥" },
  { value: 'sisters_daughter',         label: "Sister's Daughter (Niece) — 外甥女" },
];

const DOOR_W = 38;  // 10 % smaller than original 42
const DOOR_H = 63;  // 10 % smaller than original 70

const buildDoorGroup = (cx, cy, angle) => {
  const hw = DOOR_W / 2, hh = DOOR_H / 2;
  const panelH = DOOR_H - 14;
  const halfPanelW = (DOOR_W / 2 - 3);  // each leaf width
  const r = (obj) => ({ selectable: false, evented: false, originX: 'center', originY: 'center', ...obj });

  const frame       = new fabric.Rect(r({ width: DOOR_W,       height: DOOR_H,   left: 0,            top: 0,            fill: '#5C3310', stroke: '#3D2B1F', strokeWidth: 2 }));
  const leftLeaf    = new fabric.Rect(r({ width: halfPanelW,   height: panelH,   left: -(hw / 2),    top: 2,            fill: '#7B4E2D', stroke: '#3D2B1F', strokeWidth: 1 }));
  const rightLeaf   = new fabric.Rect(r({ width: halfPanelW,   height: panelH,   left:  (hw / 2),    top: 2,            fill: '#7B4E2D', stroke: '#3D2B1F', strokeWidth: 1 }));
  const divider     = new fabric.Rect(r({ width: 2,            height: DOOR_H - 4, left: 0,          top: 1,            fill: '#3D2B1F' }));
  const arch        = new fabric.Rect(r({ width: DOOR_W - 8,   height: 10,       left: 0,            top: -(hh - 6),    fill: '#C4A882', stroke: '#3D2B1F', strokeWidth: 1, rx: 5, ry: 5 }));
  const leftKnob    = new fabric.Circle(r({ radius: 2.5,       left: -2.5,       top: 4,                                fill: '#D4AF37', stroke: '#A08000', strokeWidth: 0.5 }));
  const rightKnob   = new fabric.Circle(r({ radius: 2.5,       left:  2.5,       top: 4,                                fill: '#D4AF37', stroke: '#A08000', strokeWidth: 0.5 }));

  return new fabric.Group([frame, leftLeaf, rightLeaf, divider, arch, leftKnob, rightKnob], {
    left: cx, top: cy, angle,
    originX: 'center', originY: 'center',
    id: 'door-entrance',
    selectable: true, hasControls: false, hasBorders: true,
    lockScalingX: true, lockScalingY: true, lockRotation: true,
  });
};

// ── Pathfinding (4-directional A* → rectilinear path) ──────────────────────
const NAV_CELL = 16;    // grid resolution in world units
const NAV_OBS_PAD = 16; // extra clearance beyond table+seat radius

function navGetObstacles(canvas) {
  return canvas.getObjects()
    .filter(o => o.id?.startsWith('table-group-'))
    .map(o => {
      const c = o.getCenterPoint();
      // table radius 30 + seat offset 25 + seat radius 10 = 65, plus clearance
      const r = (65 + NAV_OBS_PAD) * o.scaleX;
      return { cx: c.x, cy: c.y, r };
    });
}

function navSegClear(ax, ay, bx, by, obstacles) {
  const dist = Math.sqrt((bx - ax) ** 2 + (by - ay) ** 2);
  if (dist < 0.001) return true;
  const steps = Math.ceil(dist / (NAV_CELL / 2)) + 1;
  for (let k = 0; k <= steps; k++) {
    const t = k / steps;
    const px = ax + (bx - ax) * t, py = ay + (by - ay) * t;
    for (const obs of obstacles) {
      const dx = px - obs.cx, dy = py - obs.cy;
      if (dx * dx + dy * dy < obs.r * obs.r) return false;
    }
  }
  return true;
}

// Greedy any-angle string-pull: replace runs of waypoints with a single straight line (diagonal ok)
// when the direct segment is fully obstacle-free.
function navStringPull(path, obstacles) {
  if (path.length <= 2) return path;
  const result = [path[0]];
  let i = 0;
  while (i < path.length - 1) {
    let jumped = false;
    for (let j = path.length - 1; j > i; j--) {
      if (navSegClear(path[i].x, path[i].y, path[j].x, path[j].y, obstacles)) {
        result.push(path[j]); i = j; jumped = true; break;
      }
    }
    if (!jumped) { result.push(path[i + 1]); i++; }
  }
  return result;
}

// Greedy rectilinear string-pull: replace multi-turn runs with a single L when open space allows.
function navSmoothPath(path, obstacles) {
  if (path.length <= 2) return path;
  const result = [path[0]];
  let i = 0;
  while (i < path.length - 1) {
    let jumped = false;
    for (let j = path.length - 1; j > i + 1; j--) {
      const a = path[i], b = path[j];
      // Try horizontal-first L: a → (b.x, a.y) → b
      const hx = b.x, hy = a.y;
      if (navSegClear(a.x, a.y, hx, hy, obstacles) && navSegClear(hx, hy, b.x, b.y, obstacles)) {
        if (Math.abs(hx - a.x) > 0.5 && Math.abs(hy - b.y) > 0.5) result.push({ x: hx, y: hy });
        result.push(b); i = j; jumped = true; break;
      }
      // Try vertical-first L: a → (a.x, b.y) → b
      const vx = a.x, vy = b.y;
      if (navSegClear(a.x, a.y, vx, vy, obstacles) && navSegClear(vx, vy, b.x, b.y, obstacles)) {
        if (Math.abs(vx - b.x) > 0.5 && Math.abs(vy - a.y) > 0.5) result.push({ x: vx, y: vy });
        result.push(b); i = j; jumped = true; break;
      }
    }
    if (!jumped) { result.push(path[i + 1]); i++; }
  }
  return result;
}

function navBuildPath(canvas, startX, startY, endX, endY) {
  const obstacles = navGetObstacles(canvas);
  const CELL = NAV_CELL;
  const MARGIN = 100;

  const allCX = obstacles.map(o => o.cx);
  const allCY = obstacles.map(o => o.cy);
  const minX = Math.min(startX, endX, ...allCX) - MARGIN;
  const minY = Math.min(startY, endY, ...allCY) - MARGIN;
  const maxX = Math.max(startX, endX, ...allCX) + MARGIN;
  const maxY = Math.max(startY, endY, ...allCY) + MARGIN;
  const cols = Math.ceil((maxX - minX) / CELL);
  const rows = Math.ceil((maxY - minY) / CELL);

  // Mark obstacle cells
  const blocked = new Uint8Array(cols * rows);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const wx = minX + (c + 0.5) * CELL, wy = minY + (r + 0.5) * CELL;
      for (const obs of obstacles) {
        const dx = wx - obs.cx, dy = wy - obs.cy;
        if (dx * dx + dy * dy <= obs.r * obs.r) { blocked[r * cols + c] = 1; break; }
      }
    }
  }

  const wToG = (wx, wy) => ({
    c: Math.max(0, Math.min(cols - 1, Math.floor((wx - minX) / CELL))),
    r: Math.max(0, Math.min(rows - 1, Math.floor((wy - minY) / CELL))),
  });
  const gToW = (gc, gr) => ({ x: minX + (gc + 0.5) * CELL, y: minY + (gr + 0.5) * CELL });

  const sg = wToG(startX, startY), eg = wToG(endX, endY);
  const si = sg.r * cols + sg.c, ei = eg.r * cols + eg.c;
  blocked[si] = 0; blocked[ei] = 0;

  // 4-directional only → naturally rectilinear path
  const DIRS = [[0,-1],[1,0],[0,1],[-1,0]];
  const gCost = new Float32Array(cols * rows).fill(1e9);
  const parent = new Int32Array(cols * rows).fill(-1);
  const closed = new Uint8Array(cols * rows);

  const heu = (c, r) => Math.abs(eg.c - c) + Math.abs(eg.r - r); // Manhattan heuristic

  const heap = [];
  const hPush = (f, idx) => {
    heap.push([f, idx]);
    let i = heap.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (heap[p][0] <= heap[i][0]) break;
      [heap[p], heap[i]] = [heap[i], heap[p]]; i = p;
    }
  };
  const hPop = () => {
    const top = heap[0]; const last = heap.pop();
    if (heap.length) {
      heap[0] = last; let i = 0;
      for (;;) {
        const l = 2*i+1, r2 = 2*i+2; let s = i;
        if (l < heap.length && heap[l][0] < heap[s][0]) s = l;
        if (r2 < heap.length && heap[r2][0] < heap[s][0]) s = r2;
        if (s === i) break;
        [heap[s], heap[i]] = [heap[i], heap[s]]; i = s;
      }
    }
    return top;
  };

  gCost[si] = 0; hPush(heu(sg.c, sg.r), si);
  let found = false;
  while (heap.length) {
    const [, idx] = hPop();
    if (closed[idx]) continue;
    closed[idx] = 1;
    if (idx === ei) { found = true; break; }
    const cc = idx % cols, cr = Math.floor(idx / cols);
    for (const [dc, dr] of DIRS) {
      const nc = cc + dc, nr = cr + dr;
      if (nc < 0 || nc >= cols || nr < 0 || nr >= rows) continue;
      const ni = nr * cols + nc;
      if (closed[ni] || blocked[ni]) continue;
      const ng = gCost[idx] + 1;
      if (ng < gCost[ni]) { gCost[ni] = ng; parent[ni] = idx; hPush(ng + heu(nc, nr), ni); }
    }
  }

  if (!found) return [{ x: startX, y: startY }, { x: endX, y: endY }];

  // Reconstruct grid path
  const indices = []; let cur = ei;
  while (cur !== -1) { indices.unshift(cur); cur = parent[cur]; }
  const raw = indices.map(i => gToW(i % cols, Math.floor(i / cols)));
  raw[0] = { x: startX, y: startY };
  raw[raw.length - 1] = { x: endX, y: endY };

  // Remove collinear intermediate points (keep only corners)
  const corners = [raw[0]];
  for (let i = 1; i < raw.length - 1; i++) {
    const prev = corners[corners.length - 1], curr = raw[i], next = raw[i + 1];
    const cross = (curr.x - prev.x) * (next.y - curr.y) - (curr.y - prev.y) * (next.x - curr.x);
    if (cross !== 0) corners.push(curr);
  }
  corners.push(raw[raw.length - 1]);
  return navStringPull(navSmoothPath(corners, obstacles), obstacles);
}

function Reception({ groomName, setGroomName, brideName, setBrideName, eventId = 1, eventDate, eventVenue, pendingGuestRole, onPendingRoleDone, onPendingGuest, pendingVenueForEvent, onVenueForEventSelected, onVenueReady, onDateSet, onVenueSet, onCoupleSet }) {
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showVenueModal, setShowVenueModal] = useState(false);
  const [venueName, setVenueName] = useState(eventVenue ?? '');
  const [venueAddress, setVenueAddress] = useState('');
  // Sync venue name from events table after async load (eventVenue arrives after mount)
  useEffect(() => {
    if (eventVenue) setVenueName(eventVenue);
  }, [eventVenue]);
  const [venueForm, setVenueForm] = useState({ name: '', address: '' });
  const [venueAddressLoading, setVenueAddressLoading] = useState(false);
  const [venueResults, setVenueResults] = useState([]);
  const [guests, setGuests] = useState([]);
  const [selectedGuests, setSelectedGuests] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const selectModeRef = useRef(false);
  const [guestGroups, setGuestGroups] = useState(new Map()); // groupId → string[]
  const guestGroupsRef = useRef(new Map());
  const syncTextToGroupRef = useRef(null);
  const saveViewportRef = useRef(null);
  const navObjectsRef = useRef([]);
  const navAnimRef = useRef(null);
  const viewportSaveTimerRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const [tableContextMenu, setTableContextMenu] = useState(null); // { x, y, tableGroup }
  const [showDeleteTableModal, setShowDeleteTableModal] = useState(false);
  const [showRemoveAllTablesModal, setShowRemoveAllTablesModal] = useState(false);
  const [showRemoveAllGuestsModal, setShowRemoveAllGuestsModal] = useState(false);
  const tableToDeleteRef = useRef(null);
  const [editingGuest, setEditingGuest] = useState(null);
  const [guestContextMenu, setGuestContextMenu] = useState(null); // { x, y, guest }
  const guestLongPressTimerRef = useRef(null);
  const guestLongPressPosRef = useRef({ x: 0, y: 0 });
  const guestMenuJustOpenedRef = useRef(false);
  const [guestPhotoPanelOpen, setGuestPhotoPanelOpen] = useState(false);
  const [guestPhotoUrl, setGuestPhotoUrl] = useState(null);
  const photoFileInputRef = useRef(null);
  const photoFileRef = useRef(null);
  const cropImageRef = useRef(null);
  const cropCanvasRef = useRef(null);
  const cropDragRef = useRef(null);
  const cropZoomRef = useRef(1);
  const cropOffsetRef = useRef({ x: 0, y: 0 });
  const [cropZoom, setCropZoom] = useState(1);
  const [photoTooltip, setPhotoTooltip] = useState(null); // { url, x, y } | null
  const guestsRef = useRef([]);
  const [guestFilter, setGuestFilter] = useState('all');
  const [guestSearch, setGuestSearch] = useState('');
  const [nameSortDir, setNameSortDir] = useState(null); // null | 'asc' | 'desc'
  const [tableSortDir, setTableSortDir] = useState(null); // null | 'asc' | 'desc'
  const [groupSortDir, setGroupSortDir] = useState(null); // null | 'asc' | 'desc'
  const [zoom, setZoom] = useState(1);
  const zoomRef = useRef(1);
  const venueModalForEventRef = useRef(false);
  const pendingForNewEventRef = useRef(false);
  const [guestForm, setGuestForm] = useState({
    salutation: 'none',
    firstName: '',
    middleName: '',
    lastName: '',
    category: 'mutual',
    relationship: ''
  });

  const [totalSeats, setTotalSeats] = useState(0);
  const [tableCount, setTableCount] = useState(0);
  const [tablesToAdd, setTablesToAdd] = useState(1);
  const [selectOpen, setSelectOpen] = useState(false);
  const selectRef = useRef(null);

  const today = new Date();
  const initDate = eventDate ? (() => { const [y,m,d] = eventDate.split('-').map(Number); return new Date(y, m-1, d); })() : null;
  const [displayDate, setDisplayDate] = useState(initDate);
  // Sync displayDate when eventDate prop arrives asynchronously after mount
  useEffect(() => {
    if (eventDate) {
      const [y, m, d] = eventDate.split('-').map(Number);
      setDisplayDate(new Date(y, m - 1, d));
    }
  }, [eventDate]);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calNav, setCalNav] = useState({ month: (initDate ?? today).getMonth(), year: (initDate ?? today).getFullYear() });
  const [pendingDate, setPendingDate] = useState(initDate ?? today);
  const calendarRef = useRef(null);

  // Map of seatId -> guestName, drives re-renders
  const [seatedGuests, setSeatedGuests] = useState(new Map());
  // Refs mirror state so event-handler closures always see the latest values
  const seatedGuestsRef = useRef(new Map());
  // Tracks { textObj, groupObj, seatObj } per seatId for position updates
  const seatTextObjectsRef = useRef(new Map());

  useEffect(() => {
    // Reserve 300px for guest list panel + 50px for padding/gap
    const canvasWidth = Math.min(Math.min(window.innerWidth * 0.8, window.innerWidth - 350), window.innerWidth - 350);
    const canvasHeight = window.innerHeight * 0.8;

    fabricCanvasRef.current = new fabric.Canvas(canvasRef.current, {
      width: canvasWidth,
      height: canvasHeight,
      backgroundColor: 'white'
    });

    const canvas = fabricCanvasRef.current;
    canvas.renderAll();
    const canvasEl = canvas.upperCanvasEl;

    // ── Helpers (defined inside useEffect so closures always reach the latest refs) ──

    // Absolute canvas position of a seat, accounting for group scale
    const getSeatAbs = (seat, group) => {
      const gc = group.getCenterPoint();
      return {
        x: gc.x + seat.left * group.scaleX,
        y: gc.y + seat.top  * group.scaleY
      };
    };

    // Sync a text label's position AND font-size to its group's current transform.
    // Text is pushed radially outward to the outer edge of the seat circle (seatRadius = 10),
    // ensuring it never extends back across the seat and towards the table.
    const syncTextToGroup = (textObj, seatObj, groupObj) => {
      const gc    = groupObj.getCenterPoint();
      const scale = groupObj.scaleX;
      const sx = seatObj.left;
      const sy = seatObj.top;
      const dist = Math.sqrt(sx * sx + sy * sy) || 1;
      const seatRadius = 10;
      const lx = sx + (sx / dist) * seatRadius;
      const ly = sy + (sy / dist) * seatRadius;
      textObj.set({
        left:    gc.x + lx * scale,
        top:     gc.y + ly * scale,
        originX: 'center',
        originY: 'center',
        fontSize: Math.max(5, 7 * scale)
      });
      textObj.setCoords();
    };
    syncTextToGroupRef.current = syncTextToGroup;

    // Apply syncTextToGroup to every label that belongs to a given group
    const syncAllTextsForGroup = (group) => {
      seatTextObjectsRef.current.forEach(({ textObj, groupObj, seatObj }) => {
        if (groupObj !== group) return;
        syncTextToGroup(textObj, seatObj, group);
      });
      canvas.renderAll();
    };

    // ── Drop from guest-list onto canvas ──

    const handleDragOver = (e) => e.preventDefault();

    // Find the first available seat on a group, excluding a given seatId
    const findAvailableSeat = (group, excludeSeatId = null) => {
      for (const child of group.getObjects()) {
        if (!child.id || !child.id.startsWith('seat-')) continue;
        if (child.id === excludeSeatId) continue;
        if (!seatedGuestsRef.current.has(child.id)) return child;
      }
      return null;
    };

    // Check if point (x,y) hits a table circle in a group
    const getGroupAtTablePoint = (x, y) => {
      for (const obj of canvas.getObjects()) {
        if (obj.type !== 'group') continue;
        const gc = obj.getCenterPoint();
        const tableChild = obj.getObjects().find(c => c.id && c.id.startsWith('table-'));
        if (!tableChild) continue;
        const hitR = tableChild.radius * obj.scaleX;
        const dist = Math.sqrt((x - gc.x) ** 2 + (y - gc.y) ** 2);
        if (dist <= hitR) return obj;
      }
      return null;
    };

    const getGroupMembersForDrop = (name) => {
      for (const [, names] of guestGroupsRef.current) {
        if (names.includes(name)) return names;
      }
      return null;
    };

    const seatGuest = (guestName, targetSeat, targetGroup) => {
      targetSeat.set({ fill: 'transparent', stroke: 'transparent' });
      targetGroup.dirty = true;
      const guestText = new fabric.FabricText(guestName, {
        left: 0, top: 0,
        fontSize: 9,
        fill: '#222',
        originX: 'center',
        originY: 'center',
        selectable: true,
        hasControls: false,
        hasBorders: false,
        lockRotation: true,
        lockScalingX: true,
        lockScalingY: true,
        id: `guest-text-${targetSeat.id}`,
        seatId: targetSeat.id,
        guestName
      });
      canvas.add(guestText);
      seatTextObjectsRef.current.set(targetSeat.id, { textObj: guestText, groupObj: targetGroup, seatObj: targetSeat });
      syncTextToGroup(guestText, targetSeat, targetGroup);
      seatedGuestsRef.current.set(targetSeat.id, guestName);
      const guestRecord = guestsRef.current.find(g => g.name === guestName);
      if (guestRecord?.id) {
        const tableNum = parseInt(targetGroup.id.replace('table-group-', ''), 10);
        const seatObjs = targetGroup.getObjects().filter(c => c.id && c.id.startsWith('seat-'));
        const seatIdx = seatObjs.indexOf(targetSeat);
        supabase.from('guests').update({ seat_id: targetSeat.id, table_number: tableNum, seat_index: seatIdx }).eq('id', guestRecord.id)
          .then(({ error }) => { if (error) console.error('seat save failed:', error.message); });
      }
    };

    const handleDrop = (e) => {
      e.preventDefault();
      const raw = e.dataTransfer.getData('text/plain');
      if (!raw) return;

      if (raw === 'door') {
        const vt   = canvas.viewportTransform;
        const zoom = vt[0], panX = vt[4], panY = vt[5];
        const cw = canvas.getWidth(), ch = canvas.getHeight();
        const halfH = DOOR_H / 2, halfW = DOOR_W / 2;
        const sx = e.offsetX, sy = e.offsetY;
        const dTop = sy, dBottom = ch - sy, dLeft = sx, dRight = cw - sx;
        const minD = Math.min(dTop, dBottom, dLeft, dRight);
        let snapSx, snapSy, angle;
        if (minD === dBottom)     { angle = 0;   snapSx = Math.max(halfW, Math.min(cw - halfW, sx)); snapSy = ch - halfH; }
        else if (minD === dTop)   { angle = 180; snapSx = Math.max(halfW, Math.min(cw - halfW, sx)); snapSy = halfH; }
        else if (minD === dLeft)  { angle = -90; snapSx = halfH;      snapSy = Math.max(halfW, Math.min(ch - halfW, sy)); }
        else                      { angle = 90;  snapSx = cw - halfH; snapSy = Math.max(halfW, Math.min(ch - halfW, sy)); }
        canvas.add(buildDoorGroup((snapSx - panX) / zoom, (snapSy - panY) / zoom, angle));
        canvas.renderAll();
        return;
      }

      let guestNames;
      try { guestNames = JSON.parse(raw); } catch { guestNames = [raw]; }
      if (!Array.isArray(guestNames) || guestNames.length === 0) return;

      const pointer = canvas.getPointer(e);
      const x = pointer.x;
      const y = pointer.y;

      if (guestNames.length === 1) {
        // Single guest — check if they belong to a group and expand to all unseated group members
        const groupMembers = getGroupMembersForDrop(guestNames[0]);
        const namesForDrop = groupMembers
          ? groupMembers.filter(n => !seatedGuestsRef.current.has([...seatedGuestsRef.current.entries()].find(([, v]) => v === n)?.[0] ?? ''))
          : [guestNames[0]];
        // Filter to truly unseated names
        const seatedNames = new Set(seatedGuestsRef.current.values());
        const unseatedForDrop = namesForDrop.filter(n => !seatedNames.has(n));

        if (unseatedForDrop.length > 1) {
          // Multiple group members — must drop on a table; need enough free seats
          const group = getGroupAtTablePoint(x, y);
          if (!group) return;
          const availableSeats = group.getObjects().filter(
            c => c.id && c.id.startsWith('seat-') && !seatedGuestsRef.current.has(c.id)
          );
          if (availableSeats.length < unseatedForDrop.length) return;
          unseatedForDrop.forEach((name, i) => seatGuest(name, availableSeats[i], group));
        } else {
          // Single guest — hit-test seat first, then table
          let targetSeat = null;
          let targetGroup = null;
          for (const obj of canvas.getObjects()) {
            if (obj.type !== 'group') continue;
            for (const child of obj.getObjects()) {
              if (!child.id || !child.id.startsWith('seat-')) continue;
              if (seatedGuestsRef.current.has(child.id)) continue;
              const abs  = getSeatAbs(child, obj);
              const hitR = (child.radius + 8) * obj.scaleX;
              const dist = Math.sqrt((x - abs.x) ** 2 + (y - abs.y) ** 2);
              if (dist <= hitR) { targetSeat = child; targetGroup = obj; break; }
            }
            if (targetSeat) break;
          }
          if (!targetSeat) {
            const group = getGroupAtTablePoint(x, y);
            if (group) { const seat = findAvailableSeat(group); if (seat) { targetSeat = seat; targetGroup = group; } }
          }
          if (!targetSeat) return;
          seatGuest(unseatedForDrop[0] ?? guestNames[0], targetSeat, targetGroup);
        }
      } else {
        // Multiple guests — must drop on a table; need enough free seats
        const group = getGroupAtTablePoint(x, y);
        if (!group) return;

        // Collect all available seats on this table
        const availableSeats = group.getObjects().filter(
          c => c.id && c.id.startsWith('seat-') && !seatedGuestsRef.current.has(c.id)
        );
        if (availableSeats.length < guestNames.length) return; // insufficient seats — do nothing

        guestNames.forEach((name, i) => seatGuest(name, availableSeats[i], group));
      }

      setSeatedGuests(new Map(seatedGuestsRef.current));
      canvas.renderAll();
      // Successful seating — release select mode (group is preserved)
      selectModeRef.current = false;
      setSelectMode(false);
      setSelectedGuests([]);
    };

    canvasEl.addEventListener('dragover', handleDragOver);
    canvasEl.addEventListener('drop', handleDrop);

    // ── Keep labels tracking with their table while it is moved or scaled ──

    canvas.on('object:moving', (e) => {
      if (e.target?.id?.startsWith('door-')) {
        const obj = e.target;
        const vt  = canvas.viewportTransform;
        const zoom = vt[0], panX = vt[4], panY = vt[5];
        const cw = canvas.getWidth(), ch = canvas.getHeight();
        const halfH = DOOR_H / 2, halfW = DOOR_W / 2;

        // Work in screen space so comparisons are viewport-independent
        const sx = obj.left * zoom + panX;
        const sy = obj.top  * zoom + panY;
        const dTop = sy, dBottom = ch - sy, dLeft = sx, dRight = cw - sx;
        const minD = Math.min(dTop, dBottom, dLeft, dRight);

        let snapSx, snapSy, angle;
        if (minD === dBottom) {
          snapSx = Math.max(halfW, Math.min(cw - halfW, sx)); snapSy = ch - halfH; angle = 0;
        } else if (minD === dTop) {
          snapSx = Math.max(halfW, Math.min(cw - halfW, sx)); snapSy = halfH;      angle = 180;
        } else if (minD === dLeft) {
          snapSx = halfH; snapSy = Math.max(halfW, Math.min(ch - halfW, sy));      angle = -90;
        } else {
          snapSx = cw - halfH; snapSy = Math.max(halfW, Math.min(ch - halfW, sy)); angle = 90;
        }

        // Convert snapped screen position back to world coordinates
        obj.set({ left: (snapSx - panX) / zoom, top: (snapSy - panY) / zoom, angle });
        return;
      }
      if (e.target?.type === 'group') syncAllTextsForGroup(e.target);
    });
    canvas.on('before:render', () => {
      const door = canvas.getObjects().find(o => o.id === 'door-entrance');
      if (!door) return;
      const vt = canvas.viewportTransform;
      if (!vt) return;
      const zoom = vt[0], panX = vt[4], panY = vt[5];
      if (!zoom) return;
      const cw = canvas.getWidth(), ch = canvas.getHeight();
      if (!cw || !ch) return;
      const halfH = DOOR_H / 2, halfW = DOOR_W / 2;
      const angle = door.angle ?? 0;
      let snapSx, snapSy;
      if (Math.abs(angle) < 45) {           // bottom (0°)
        snapSx = Math.max(halfW, Math.min(cw - halfW, door.left * zoom + panX));
        snapSy = ch - halfH;
      } else if (Math.abs(Math.abs(angle) - 180) < 45) { // top (180° or -180°)
        snapSx = Math.max(halfW, Math.min(cw - halfW, door.left * zoom + panX));
        snapSy = halfH;
      } else if (angle < 0) {               // left (-90°)
        snapSx = halfH;
        snapSy = Math.max(halfW, Math.min(ch - halfW, door.top * zoom + panY));
      } else {                              // right (90°)
        snapSx = cw - halfH;
        snapSy = Math.max(halfW, Math.min(ch - halfW, door.top * zoom + panY));
      }
      door.set({ left: (snapSx - panX) / zoom, top: (snapSy - panY) / zoom });
      door.setCoords();
    });

    canvas.on('object:scaling', (e) => { if (e.target?.type === 'group') syncAllTextsForGroup(e.target); });
    canvas.on('object:modified', (e) => {
      const obj = e.target;
      if (obj?.type !== 'group') return;
      syncAllTextsForGroup(obj);
      if (obj.id?.startsWith('table-group-')) {
        const tableNum = parseInt(obj.id.replace('table-group-', ''), 10);
        supabase.from('tables').update({
          left_pos: obj.left, top_pos: obj.top,
          scale_x: obj.scaleX, scale_y: obj.scaleY
        }).eq('table_number', tableNum)
          .then(({ error }) => { if (error) console.error('table position save failed:', error.message); });
      } else if (obj.id === 'door-entrance') {
        supabase.from('canvas_state').upsert({
          id: eventId, door_left: obj.left, door_top: obj.top, door_angle: obj.angle
        }).then(({ error }) => { if (error) console.error('door save failed:', error.message); });
      }
    });

    canvas.on('mouse:dblclick', (e) => {
      if (e.target?.id?.startsWith('door-')) { canvas.remove(e.target); canvas.renderAll(); }
    });

    canvas.on('mouse:over', (e) => {
      const obj = e.target;
      if (!obj || !obj.guestName) return;
      const guest = guestsRef.current.find(g => g.name === obj.guestName);
      if (!guest?.photo) return;
      const nativeEvent = e.e;
      if (!nativeEvent) return;
      setPhotoTooltip({ url: guest.photo, name: guest.name, x: nativeEvent.clientX, y: nativeEvent.clientY });
    });

    canvas.on('mouse:move', (e) => {
      const obj = e.target;
      if (!obj || !obj.guestName) return;
      const nativeEvent = e.e;
      if (!nativeEvent) return;
      setPhotoTooltip(prev => prev ? { ...prev, x: nativeEvent.clientX, y: nativeEvent.clientY } : prev);
    });

    canvas.on('mouse:out', (e) => {
      if (e.target?.guestName) setPhotoTooltip(null);
    });

    // ── Helper: restore a seat circle to its original appearance ──
    const restoreSeat = (seatId) => {
      const entry = seatTextObjectsRef.current.get(seatId);
      if (!entry) return;
      entry.seatObj.set({ fill: '#e0e0e0', stroke: '#666', strokeWidth: 1 });
      entry.groupObj.dirty = true;
    };

    // ── Drag guest-text back to guest-list, or to another seat ──

    canvas.on('mouse:up', (e) => {
      const obj = canvas.getActiveObject();
      if (!obj || !obj.seatId) return;
      const nativeEvent = e.e;
      if (!nativeEvent) return;

      // Check if released over the guest list
      const guestListEl = document.querySelector('.guest-list-box');
      if (guestListEl) {
        const rect = guestListEl.getBoundingClientRect();
        const overList =
          nativeEvent.clientX >= rect.left &&
          nativeEvent.clientX <= rect.right &&
          nativeEvent.clientY >= rect.top  &&
          nativeEvent.clientY <= rect.bottom;
        if (overList) {
          const seatId = obj.seatId;
          restoreSeat(seatId);
          canvas.remove(obj);
          canvas.discardActiveObject();
          const unseatedName = obj.guestName;
          seatTextObjectsRef.current.delete(seatId);
          seatedGuestsRef.current.delete(seatId);
          setSeatedGuests(new Map(seatedGuestsRef.current));
          canvas.renderAll();
          const guestRecord = guestsRef.current.find(g => g.name === unseatedName);
          if (guestRecord?.id) {
            supabase.from('guests').update({ seat_id: null, table_number: null, seat_index: null, arrived: false }).eq('id', guestRecord.id);
            guestsRef.current = guestsRef.current.map(g => g.id === guestRecord.id ? { ...g, arrived: false } : g);
            setGuests(prev => prev.map(g => g.id === guestRecord.id ? { ...g, arrived: false } : g));
          }
          return;
        }
      }

      // Check if released over a different, unoccupied seat
      const center   = obj.getCenterPoint();
      let targetSeat  = null;
      let targetGroup = null;
      for (const canvasObj of canvas.getObjects()) {
        if (canvasObj.type !== 'group') continue;
        for (const child of canvasObj.getObjects()) {
          if (!child.id || !child.id.startsWith('seat-')) continue;
          if (child.id === obj.seatId) continue;           // same seat
          if (seatedGuestsRef.current.has(child.id)) continue; // occupied
          const abs  = getSeatAbs(child, canvasObj);
          const hitR = (child.radius + 8) * canvasObj.scaleX;
          const dist = Math.sqrt((center.x - abs.x) ** 2 + (center.y - abs.y) ** 2);
          if (dist <= hitR) { targetSeat = child; targetGroup = canvasObj; break; }
        }
        if (targetSeat) break;
      }

      // If no seat hit, check if dropped on a table circle
      if (!targetSeat) {
        const group = getGroupAtTablePoint(center.x, center.y);
        if (group) {
          const seat = findAvailableSeat(group, obj.seatId);
          if (seat) { targetSeat = seat; targetGroup = group; }
        }
      }

      if (targetSeat) {
        // Move guest from old seat to new seat
        const oldSeatId = obj.seatId;
        restoreSeat(oldSeatId);

        targetSeat.set({ fill: 'transparent', stroke: 'transparent' });
        targetGroup.dirty = true;

        obj.set({ id: `guest-text-${targetSeat.id}`, seatId: targetSeat.id });
        seatTextObjectsRef.current.delete(oldSeatId);
        seatTextObjectsRef.current.set(targetSeat.id, {
          textObj: obj, groupObj: targetGroup, seatObj: targetSeat
        });
        seatedGuestsRef.current.delete(oldSeatId);
        seatedGuestsRef.current.set(targetSeat.id, obj.guestName);
        setSeatedGuests(new Map(seatedGuestsRef.current));
        syncTextToGroup(obj, targetSeat, targetGroup);
        canvas.renderAll();
        const moveRecord = guestsRef.current.find(g => g.name === obj.guestName);
        if (moveRecord?.id) {
          const tableNum = parseInt(targetGroup.id.replace('table-group-', ''), 10);
          const seatObjs = targetGroup.getObjects().filter(c => c.id && c.id.startsWith('seat-'));
          const seatIdx = seatObjs.indexOf(targetSeat);
          supabase.from('guests').update({ seat_id: targetSeat.id, table_number: tableNum, seat_index: seatIdx }).eq('id', moveRecord.id);
        }
      } else {
        // Snap text back to its seat
        const entry = seatTextObjectsRef.current.get(obj.seatId);
        if (entry) {
          syncTextToGroup(obj, entry.seatObj, entry.groupObj);
          canvas.renderAll();
        }
      }
    });

    // ── Pan: drag on empty canvas space ──
    let isPanning = false;
    let panLastX = 0;
    let panLastY = 0;
    let longPressOriginX = 0;
    let longPressOriginY = 0;

    canvas.on('mouse:down', (opt) => {
      const target = opt.target;

      // Long press on a table group → start 2-second timer
      if (target?.id?.startsWith('table-group-')) {
        const e = opt.e;
        longPressOriginX = e.clientX ?? 0;
        longPressOriginY = e.clientY ?? 0;
        longPressTimerRef.current = setTimeout(() => {
          setTableContextMenu({ x: e.clientX, y: e.clientY, tableGroup: target });
        }, 2000);
        return;
      }

      // Long press on a seated guest text → start 600ms timer
      if (target?.id?.startsWith('guest-text-')) {
        const e = opt.e;
        const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
        const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
        longPressOriginX = clientX;
        longPressOriginY = clientY;
        longPressTimerRef.current = setTimeout(() => {
          longPressTimerRef.current = null;
          const guestRecord = guestsRef.current.find(g => g.name === target.guestName);
          if (guestRecord) {
            guestMenuJustOpenedRef.current = true;
            setGuestContextMenu({ x: clientX, y: clientY, guest: guestRecord, isSeated: true });
          }
        }, 600);
        return;
      }

      if (target) return; // other object — let fabric handle it
      isPanning = true;
      const e = opt.e;
      panLastX = e.clientX ?? (e.touches?.[0]?.clientX ?? 0);
      panLastY = e.clientY ?? (e.touches?.[0]?.clientY ?? 0);
      canvas.setCursor('grabbing');
      canvas.selection = false;
    });

    canvas.on('mouse:move', (opt) => {
      // Cancel long press if mouse drifts more than 5px
      if (longPressTimerRef.current) {
        const e = opt.e;
        const dx = (e.clientX ?? 0) - longPressOriginX;
        const dy = (e.clientY ?? 0) - longPressOriginY;
        if (Math.sqrt(dx * dx + dy * dy) > 5) {
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
        }
      }
      if (!isPanning) return;
      const e = opt.e;
      const clientX = e.clientX ?? (e.touches?.[0]?.clientX ?? 0);
      const clientY = e.clientY ?? (e.touches?.[0]?.clientY ?? 0);
      const dx = clientX - panLastX;
      const dy = clientY - panLastY;
      canvas.relativePan({ x: dx, y: dy });
      panLastX = clientX;
      panLastY = clientY;
    });

    canvas.on('mouse:up', () => {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
      if (!isPanning) return;
      isPanning = false;
      canvas.setCursor('default');
      canvas.selection = true;
      saveViewport();
    });

    // ── Zoom: mouse wheel ──
    const MIN_ZOOM = 0.2;
    const MAX_ZOOM = 4;

    const saveViewport = () => {
      clearTimeout(viewportSaveTimerRef.current);
      viewportSaveTimerRef.current = setTimeout(() => {
        supabase.from('canvas_state').upsert({ id: eventId, viewport: canvas.viewportTransform })
          .then(({ error }) => { if (error) console.error('viewport save failed:', error.message); });
      }, 500);
    };
    saveViewportRef.current = saveViewport;

    const handleWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoomRef.current * delta));
      const point = canvas.getPointer(e);
      canvas.zoomToPoint({ x: point.x, y: point.y }, newZoom);
      zoomRef.current = newZoom;
      setZoom(newZoom);
      saveViewport();
    };

    // ── Zoom: pinch gesture (touch) / Pan: single-finger on empty space ──
    let lastDist = null;
    let touchPanLastX = 0;
    let touchPanLastY = 0;

    const handleTouchStart = (e) => {
      if (e.touches.length === 2) {
        lastDist = null;
      } else if (e.touches.length === 1) {
        touchPanLastX = e.touches[0].clientX;
        touchPanLastY = e.touches[0].clientY;
      }
    };
    const handleTouchMove = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (lastDist !== null) {
          const delta = dist / lastDist;
          const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoomRef.current * delta));
          const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
          const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
          const rect = canvasEl.getBoundingClientRect();
          canvas.zoomToPoint({ x: midX - rect.left, y: midY - rect.top }, newZoom);
          zoomRef.current = newZoom;
          setZoom(newZoom);
        }
        lastDist = dist;
      } else if (e.touches.length === 1 && isPanning) {
        e.preventDefault();
        const tx = e.touches[0].clientX;
        const ty = e.touches[0].clientY;
        canvas.relativePan({ x: tx - touchPanLastX, y: ty - touchPanLastY });
        touchPanLastX = tx;
        touchPanLastY = ty;
      }
    };
    const handleTouchEnd = () => { lastDist = null; };

    canvasEl.addEventListener('wheel', handleWheel, { passive: false });
    canvasEl.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvasEl.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvasEl.addEventListener('touchend', handleTouchEnd);

    return () => {
      canvasEl.removeEventListener('dragover', handleDragOver);
      canvasEl.removeEventListener('drop', handleDrop);
      canvasEl.removeEventListener('wheel', handleWheel);
      canvasEl.removeEventListener('touchstart', handleTouchStart);
      canvasEl.removeEventListener('touchmove', handleTouchMove);
      canvasEl.removeEventListener('touchend', handleTouchEnd);
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
      }
    };
  // eventId is stable per mount (component remounts via key when it changes)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showVenueModal || !venueForm.name.trim()) { setVenueResults([]); return; }
    setVenueAddressLoading(true);
    const timer = setTimeout(() => {
      const doLookup = async (lat, lon) => {
        try {
          const params = new URLSearchParams({
            q: venueForm.name,
            format: 'json',
            limit: '5',
            addressdetails: '1',
            ...(lat != null && { lat: String(lat), lon: String(lon), bounded: '0' })
          });
          const res = await fetch(
            `https://nominatim.openstreetmap.org/search?${params}`,
            { headers: { 'Accept-Language': 'en' } }
          );
          const data = await res.json();
          const results = data.map(item => {
            const { house_number, road, city, town, village, state, postcode, country } = item.address;
            const place = city || town || village || '';
            const street = [house_number, road].filter(Boolean).join(' ');
            const address = [street, place, state, postcode].filter(Boolean).join(', ');
            const displayName = item.name || item.display_name.split(',')[0];
            const subtitle = [place, state, country].filter(Boolean).join(', ');
            return { displayName, address, subtitle };
          });
          setVenueResults(results);
        } catch (_) {}
        setVenueAddressLoading(false);
      };

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          pos => doLookup(pos.coords.latitude, pos.coords.longitude),
          ()  => doLookup(null, null)
        );
      } else {
        doLookup(null, null);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [venueForm.name, showVenueModal]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (selectRef.current && !selectRef.current.contains(e.target)) {
        setSelectOpen(false);
      }
      if (calendarRef.current && !calendarRef.current.contains(e.target)) {
        setCalendarOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (guestContextMenu)      { setGuestContextMenu(null);      return; }
      if (tableContextMenu)      { setTableContextMenu(null);      return; }
      if (showDeleteTableModal)      { setShowDeleteTableModal(false); return; }
      if (showRemoveAllTablesModal)  { setShowRemoveAllTablesModal(false); return; }
      if (showRemoveAllGuestsModal)  { setShowRemoveAllGuestsModal(false); return; }
      if (showVenueModal) { setShowVenueModal(false); return; }
      if (showResetModal) { setShowResetModal(false); return; }
      if (calendarOpen)   { setCalendarOpen(false);   return; }
      if (showModal)      { setShowModal(false); setGuestPhotoUrl(null); setGuestPhotoPanelOpen(false); resetCropState(); setEditingGuest(null); return; }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [guestContextMenu, tableContextMenu, showDeleteTableModal, showRemoveAllTablesModal, showRemoveAllGuestsModal, showVenueModal, showResetModal, calendarOpen, showModal]);

  useEffect(() => { selectModeRef.current = selectMode; }, [selectMode]);
  useEffect(() => { guestGroupsRef.current = guestGroups; }, [guestGroups]);
  useEffect(() => { guestsRef.current = guests; }, [guests]);

  // Auto-group the couple whenever both groom and bride are set
  useEffect(() => {
    if (!groomName || !brideName) {
      // Remove any group that contains the currently set name
      const nameToCheck = groomName || brideName;
      if (!nameToCheck) return;
      const toRemove = [];
      for (const [id, names] of guestGroupsRef.current) {
        if (names.includes(nameToCheck)) toRemove.push(id);
      }
      if (toRemove.length === 0) return;
      toRemove.forEach(id => supabase.from('guests').update({ group_id: null }).eq('group_id', id));
      setGuestGroups(prev => { const next = new Map(prev); toRemove.forEach(id => next.delete(id)); return next; });
      toRemove.forEach(id => guestGroupsRef.current.delete(id));
      return;
    }
    // Both set — ensure they are in a group together
    for (const [, names] of guestGroupsRef.current) {
      if (names.includes(groomName) && names.includes(brideName)) return; // already grouped
    }
    // Remove any existing groups for either
    const toRemove = [];
    for (const [id, names] of guestGroupsRef.current) {
      if (names.includes(groomName) || names.includes(brideName)) toRemove.push(id);
    }
    toRemove.forEach(id => supabase.from('guests').update({ group_id: null }).eq('group_id', id));
    toRemove.forEach(id => guestGroupsRef.current.delete(id));
    const newGroupId = randomUUID();
    const groomGuest = guestsRef.current.find(g => g.name === groomName);
    const brideGuest = guestsRef.current.find(g => g.name === brideName);
    const ids = [groomGuest?.id, brideGuest?.id].filter(Boolean);
    if (ids.length > 0) supabase.from('guests').update({ group_id: newGroupId }).in('id', ids);
    const newMap = new Map(guestGroupsRef.current);
    newMap.set(newGroupId, [groomName, brideName]);
    guestGroupsRef.current = newMap;
    setGuestGroups(new Map(newMap));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groomName, brideName]);

  // Open the Add Guest dialog when triggered from the Events modal
  useEffect(() => {
    if (!pendingGuestRole) return;
    const roleStr = typeof pendingGuestRole === 'object' ? pendingGuestRole.role : pendingGuestRole;
    pendingForNewEventRef.current = typeof pendingGuestRole === 'object' && !!pendingGuestRole.forNewEvent;
    setGuestForm(f => ({ ...f, relationship: roleStr, category: roleStr === 'groom' || roleStr === 'bride' ? roleStr : f.category }));
    setEditingGuest(null);
    setShowModal(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingGuestRole]);

  const venueSeededRef = useRef(false);
  useEffect(() => {
    if (!venueName || venueSeededRef.current) return;
    venueSeededRef.current = true;
    supabase.from('venues').upsert({ name: venueName, address: venueAddress || null }, { onConflict: 'name' })
      .then(({ error }) => { if (!error) onVenueReady?.(); });
  }, [venueName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open the venue dialog when triggered from the Events modal
  useEffect(() => {
    if (!pendingVenueForEvent) return;
    venueModalForEventRef.current = true;
    setVenueForm({ name: '', address: '' });
    setVenueResults([]);
    setShowVenueModal(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingVenueForEvent]);

  const syncTableColor = (tableGroup) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !tableGroup) return;
    const tableNum = parseInt(tableGroup.id.replace('table-group-', ''), 10);
    const tableCircle = tableGroup.getObjects().find(o => o.id === `table-${tableNum}`);
    if (!tableCircle) return;
    const occupiedNames = [];
    for (const [, entry] of seatTextObjectsRef.current) {
      if (entry.groupObj === tableGroup) occupiedNames.push(entry.textObj.text);
    }
    const allArrived = occupiedNames.length > 0 &&
      occupiedNames.every(name => guestsRef.current.find(g => g.name === name)?.arrived);
    tableCircle.set({ fill: allArrived ? '#d4f7d4' : '#f8f8f8' });
    tableGroup.dirty = true;
    canvas.requestRenderAll();
  };

  const syncAllTableColors = () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    for (const obj of canvas.getObjects()) {
      if (obj.id?.startsWith('table-group-')) syncTableColor(obj);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const restore = async () => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;

      // Guard against React Strict Mode double-invocation
      if (canvas.getObjects().some(o => o.id?.startsWith('table-group-') || o.id === 'door-entrance')) return;

      const [tablesResult, stateResult, guestsResult] = await Promise.all([
        supabase.from('tables').select('*').eq('event_id', eventId).order('table_number', { ascending: true }),
        supabase.from('canvas_state').select('*').eq('id', eventId).maybeSingle(),
        supabase.from('guests').select('*').eq('event_id', eventId),
      ]);

      if (cancelled) return;

      if (tablesResult.error) console.error('tables fetch error:', tablesResult.error.message);
      if (guestsResult.error) console.error('guests fetch error:', guestsResult.error.message);

      const tablesData = tablesResult.data;
      const stateData  = stateResult.data;
      const guestsData = guestsResult.data;

      // Restore tables first (no viewport yet — work in identity space)
      let seatOffset = 0;
      if (tablesData && tablesData.length > 0) {
        for (const tbl of tablesData) {
          createTableOnCanvas(tbl.table_number, tbl.left_pos, tbl.top_pos, tbl.scale_x ?? 1, tbl.scale_y ?? 1, seatOffset);
          seatOffset += 8;
        }
        setTableCount(tablesData.length);
        setTotalSeats(tablesData.length * 8);
      }

      // Restore guests and seat them BEFORE applying viewport transform
      if (guestsData && guestsData.length > 0) {
        const loadedGuests = guestsData.map(row => ({
          id: row.id, name: row.name, category: row.category, photo: row.photo_url ?? null, relationship: row.relationship ?? '', role: row.role ?? null, arrived: row.arrived ?? false
        }));
        setGuests(loadedGuests);
        guestsRef.current = loadedGuests;

        const seated = guestsData.filter(r => r.table_number != null && r.seat_index != null);
        console.log(`[restore] ${guestsData.length} guests, ${seated.length} seated`);

        // Build a fast lookup: seat_id → { seatObj, tableGroup }
        const seatMap = new Map();
        for (const obj of canvas.getObjects()) {
          if (!obj.id?.startsWith('table-group-')) continue;
          for (const child of obj.getObjects()) {
            if (child.id?.startsWith('seat-')) seatMap.set(child.id, { seatObj: child, tableGroup: obj });
          }
        }

        const syncFn = syncTextToGroupRef.current;
        for (const row of seated) {
          // Primary: look up by seat_id (most reliable)
          // Fallback: find by table_number + seat_index
          let entry = row.seat_id ? seatMap.get(row.seat_id) : null;
          if (!entry) {
            const grp = canvas.getObjects().find(o => o.id === `table-group-${row.table_number}`);
            if (grp) {
              const seats = grp.getObjects().filter(c => c.id?.startsWith('seat-'));
              const s = seats[row.seat_index];
              if (s) entry = { seatObj: s, tableGroup: grp };
            }
          }
          if (!entry) { console.warn(`[restore] seat not found for guest "${row.name}" (seat_id=${row.seat_id}, table=${row.table_number}, idx=${row.seat_index})`); continue; }

          const { seatObj, tableGroup } = entry;
          seatObj.set({ fill: 'transparent', stroke: 'transparent' });
          tableGroup.dirty = true;

          const guestText = new fabric.FabricText(row.name, {
            left: 0, top: 0,
            fontSize: 9, fill: '#222',
            originX: 'center', originY: 'center',
            selectable: true, hasControls: false, hasBorders: false,
            lockRotation: true, lockScalingX: true, lockScalingY: true,
            id: `guest-text-${seatObj.id}`, seatId: seatObj.id, guestName: row.name
          });
          canvas.add(guestText);
          seatTextObjectsRef.current.set(seatObj.id, { textObj: guestText, groupObj: tableGroup, seatObj });
          seatedGuestsRef.current.set(seatObj.id, row.name);
          if (syncFn) syncFn(guestText, seatObj, tableGroup);
        }
        setSeatedGuests(new Map(seatedGuestsRef.current));

        // Rebuild groups map from group_id
        const groupsMap = new Map();
        for (const row of guestsData) {
          if (!row.group_id) continue;
          if (!groupsMap.has(row.group_id)) groupsMap.set(row.group_id, []);
          groupsMap.get(row.group_id).push(row.name);
        }
        // Ensure couple is grouped — do this before setting group state so it's consistent
        const groomRow = guestsData.find(r => r.role === 'groom');
        const brideRow = guestsData.find(r => r.role === 'bride');
        if (groomRow && brideRow) {
          const alreadyGrouped = [...groupsMap.values()].some(names =>
            names.includes(groomRow.name) && names.includes(brideRow.name)
          );
          if (!alreadyGrouped) {
            const newGroupId = randomUUID();
            const ids = [groomRow.id, brideRow.id].filter(Boolean);
            if (ids.length > 0) {
              await supabase.from('guests').update({ group_id: newGroupId }).in('id', ids);
            }
            groupsMap.set(newGroupId, [groomRow.name, brideRow.name]);
          }
        }

        if (groupsMap.size > 0) {
          guestGroupsRef.current = groupsMap;
          setGuestGroups(new Map(groupsMap));
        }

        if (groomRow) setGroomName(groomRow.name);
        if (brideRow) setBrideName(brideRow.name);
      }

      // Restore venue address from canvas_state (name is authoritative from events table)
      if (stateData?.venue_address) setVenueAddress(stateData.venue_address);

      // Apply viewport AFTER all objects are placed
      if (stateData?.viewport) {
        let vt = stateData.viewport;
        if (!Array.isArray(vt)) vt = [vt[0], vt[1], vt[2], vt[3], vt[4], vt[5]];
        if (vt.length === 6 && vt.every(v => typeof v === 'number' && isFinite(v))) {
          canvas.setViewportTransform(vt);
          zoomRef.current = vt[0];
          setZoom(vt[0]);
        }
      }

      // Place door: use saved position if available, otherwise default to bottom-centre
      if (!canvas.getObjects().some(o => o.id === 'door-entrance')) {
        if (stateData?.door_left != null && stateData?.door_top != null) {
          canvas.add(buildDoorGroup(stateData.door_left, stateData.door_top, stateData.door_angle ?? 0));
        } else {
          const vt = canvas.viewportTransform;
          const zoom = vt[0], panX = vt[4], panY = vt[5];
          const cw = canvas.getWidth() || Math.min(window.innerWidth * 0.8, window.innerWidth - 350);
          const ch = canvas.getHeight() || window.innerHeight * 0.8;
          const doorWorldX = (cw / 2 - panX) / zoom;
          const doorWorldY = (ch - panY) / zoom - DOOR_H / 2;
          canvas.add(buildDoorGroup(doorWorldX, doorWorldY, 0));
        }
      }

      syncAllTableColors();
      canvas.requestRenderAll();
    };
    restore();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const createTableOnCanvas = (tableNumber, leftPos, topPos, scaleX = 1, scaleY = 1, seatOffset = 0) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return null;
    const tableRadius = 30;
    const seatRadius = 10;
    const numSeats = 8;

    const table = new fabric.Circle({
      radius: tableRadius, fill: '#f8f8f8', stroke: '#333', strokeWidth: 2,
      left: 0, top: 0, originX: 'center', originY: 'center',
      id: `table-${tableNumber}`
    });
    const seats = Array.from({ length: numSeats }, (_, i) => {
      const angle = (2 * Math.PI * i) / numSeats;
      return new fabric.Circle({
        radius: seatRadius, fill: '#e0e0e0', stroke: '#666', strokeWidth: 1,
        left: (tableRadius + 25) * Math.cos(angle),
        top:  (tableRadius + 25) * Math.sin(angle),
        originX: 'center', originY: 'center',
        id: `seat-${seatOffset + i + 1}`
      });
    });
    const tableLabel = new fabric.FabricText(String(tableNumber), {
      left: 0, top: 0, originX: 'center', originY: 'center',
      fontSize: 16, fontWeight: 'bold', fontFamily: 'Poppins, sans-serif',
      fill: '#333', selectable: false, evented: false,
    });
    const tableGroup = new fabric.Group([table, ...seats, tableLabel], {
      left: leftPos, top: topPos, scaleX, scaleY,
      selectable: true, hasControls: true, hasBorders: true,
      id: `table-group-${tableNumber}`
    });
    canvas.add(tableGroup);
    return tableGroup;
  };

  const addGuestTable = async () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const numSeats = 8;
    const cellSize = 150;
    const startX = 100;
    const startY = 100;
    const canvasWidth = canvas.getWidth();
    const colsPerRow = Math.max(1, Math.floor((canvasWidth - startX) / cellSize));

    const existingTables = canvas.getObjects().filter(o => o.id?.startsWith('table-group-'));
    const totalExistingSeats = existingTables.reduce((total, t) =>
      total + t.getObjects().filter(c => c.id && c.id.startsWith('seat-')).length, 0);

    const usedNumbers = new Set(existingTables.map(o => parseInt(o.id.replace('table-group-', ''), 10)));
    let nextCandidate = 1;
    const nextTableNumber = () => {
      while (usedNumbers.has(nextCandidate)) nextCandidate++;
      usedNumbers.add(nextCandidate);
      return nextCandidate++;
    };

    for (let n = 0; n < tablesToAdd; n++) {
      const tableNumber = nextTableNumber();
      const tableIndex = existingTables.length + n;
      const col = tableIndex % colsPerRow;
      const row = Math.floor(tableIndex / colsPerRow);
      const leftPos = startX + col * cellSize;
      const topPos  = startY + row * cellSize;

      createTableOnCanvas(tableNumber, leftPos, topPos, 1, 1, totalExistingSeats + n * numSeats);

      const { error: tblErr } = await supabase.from('tables').upsert({
        table_number: tableNumber, left_pos: leftPos, top_pos: topPos, scale_x: 1, scale_y: 1, event_id: eventId
      }, { onConflict: 'event_id,table_number' });
      if (tblErr) console.error('table upsert failed:', tblErr.message);
    }

    canvas.renderAll();
    setTotalSeats(prev => prev + numSeats * tablesToAdd);
    setTableCount(prev => prev + tablesToAdd);
  };

  const handleGuestFormChange = (e) => {
    const { name, value } = e.target;
    setGuestForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const drawCrop = useCallback(() => {
    const canvas = cropCanvasRef.current;
    const img = cropImageRef.current;
    if (!canvas || !img) return;
    const size = 120;
    const ctx = canvas.getContext('2d');
    const minScale = Math.max(size / img.width, size / img.height);
    const scale = cropZoomRef.current * minScale;
    const drawW = img.width * scale;
    const drawH = img.height * scale;
    const maxOX = Math.max(0, (drawW - size) / 2);
    const maxOY = Math.max(0, (drawH - size) / 2);
    const ox = Math.max(-maxOX, Math.min(maxOX, cropOffsetRef.current.x));
    const oy = Math.max(-maxOY, Math.min(maxOY, cropOffsetRef.current.y));
    cropOffsetRef.current = { x: ox, y: oy };
    ctx.clearRect(0, 0, size, size);
    ctx.drawImage(img, size / 2 - drawW / 2 + ox, size / 2 - drawH / 2 + oy, drawW, drawH);
  }, []);

  useEffect(() => {
    if (guestPhotoPanelOpen && cropImageRef.current) drawCrop();
  }, [guestPhotoUrl, guestPhotoPanelOpen, cropZoom, drawCrop]);

  const onCropPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    cropDragRef.current = { px: e.clientX, py: e.clientY, ox: cropOffsetRef.current.x, oy: cropOffsetRef.current.y };
  };
  const onCropPointerMove = (e) => {
    if (!cropDragRef.current) return;
    const displaySize = 160;
    const outputSize = 120;
    const scale = outputSize / displaySize;
    cropOffsetRef.current = {
      x: cropDragRef.current.ox + (e.clientX - cropDragRef.current.px) * scale,
      y: cropDragRef.current.oy + (e.clientY - cropDragRef.current.py) * scale,
    };
    drawCrop();
  };
  const onCropPointerUp = () => { cropDragRef.current = null; };

  const setCropZoomValue = (z) => {
    cropZoomRef.current = z;
    setCropZoom(z);
    drawCrop();
  };

  const resetCropState = () => {
    cropImageRef.current = null;
    cropZoomRef.current = 1;
    cropOffsetRef.current = { x: 0, y: 0 };
    setCropZoom(1);
  };

  const parseGuestName = (fullName) => {
    const salutations = ['Mr', 'Mrs', 'Ms', 'Miss'];
    const parts = fullName.trim().split(/\s+/);
    let salutation = 'none';
    let remaining = parts;
    if (parts.length > 0 && salutations.includes(parts[0])) {
      salutation = parts[0].toLowerCase();
      remaining = parts.slice(1);
    }
    const firstName = remaining[0] || '';
    const lastName = remaining.length > 1 ? remaining[remaining.length - 1] : '';
    const middleName = remaining.slice(1, remaining.length > 1 ? -1 : undefined).join(' ');
    return { salutation, firstName, middleName, lastName };
  };

  const openEditGuest = (guest, overrideRelationship) => {
    const parsed = parseGuestName(guest.name);
    setGuestForm({ ...parsed, category: guest.category || 'mutual', relationship: overrideRelationship ?? guest.relationship ?? '' });
    setEditingGuest(guest);
    if (guest.photo) {
      const img = new Image();
      img.onload = () => {
        cropImageRef.current = img;
        cropZoomRef.current = 1;
        cropOffsetRef.current = { x: 0, y: 0 };
        setCropZoom(1);
        setGuestPhotoUrl(guest.photo);
      };
      img.src = guest.photo;
    }
    setShowModal(true);
  };

  const handleAddGuest = async (e) => {
    e.preventDefault();
    const { salutation, firstName, middleName, lastName } = guestForm;

    const toTitleCase = (str) => str.trim().replace(/\S+/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());

    const fullName = [
      salutation !== 'none' ? salutation.charAt(0).toUpperCase() + salutation.slice(1) : '',
      toTitleCase(firstName),
      toTitleCase(middleName),
      toTitleCase(lastName)
    ].filter(Boolean).join(' ');

    if (!firstName.trim() || !lastName.trim()) return;

    let photoBase64 = editingGuest ? (editingGuest.photo ?? null) : null;
    if (cropImageRef.current && cropCanvasRef.current) {
      drawCrop();
      photoBase64 = cropCanvasRef.current.toDataURL('image/jpeg', 0.8);
    }
    resetCropState();

    const rel = guestForm.relationship || null;
    const isCouple = rel === 'groom' || rel === 'bride';
    const roleVal = isCouple ? rel : null;

    if (pendingForNewEventRef.current) {
      // Guest belongs to the new event being created — don't insert into current event
      onPendingGuest?.({ name: fullName, category: guestForm.category, photo_url: photoBase64, relationship: rel, role: roleVal });
    } else if (editingGuest) {
      const { error } = await supabase
        .from('guests')
        .update({ name: fullName, category: guestForm.category, photo_url: photoBase64, relationship: rel, role: roleVal })
        .eq('id', editingGuest.id);
      if (!error) {
        const updated = { ...editingGuest, name: fullName, category: guestForm.category, photo: photoBase64, relationship: rel ?? '', role: roleVal };
        guestsRef.current = guestsRef.current.map(g => g.id === editingGuest.id ? updated : g);
        setGuests(prev => prev.map(g => g.id === editingGuest.id ? updated : g));
      }
      setEditingGuest(null);
    } else {
      const { data, error } = await supabase
        .from('guests')
        .insert({ name: fullName, category: guestForm.category, photo_url: photoBase64, relationship: rel, role: roleVal, event_id: eventId })
        .select()
        .single();
      const newGuest = !error && data
        ? { id: data.id, name: fullName, category: guestForm.category, photo: photoBase64, relationship: rel ?? '', role: roleVal }
        : { name: fullName, category: guestForm.category, photo: photoBase64, relationship: rel ?? '', role: roleVal };
      guestsRef.current = [...guestsRef.current, newGuest];
      setGuests(prev => [...prev, newGuest]);
    }

    // Update groom/bride app state; clear ALL existing holders of that role first
    if (rel === 'groom') {
      const prevHolders = guestsRef.current.filter(g => g.role === 'groom' && g.name !== fullName);
      for (const prev of prevHolders) {
        if (prev.id) supabase.from('guests').update({ role: null, relationship: null }).eq('id', prev.id);
        const up = { ...prev, role: null, relationship: '' };
        guestsRef.current = guestsRef.current.map(g => g.id === prev.id ? up : g);
        setGuests(p => p.map(g => g.id === prev.id ? up : g));
      }
      setGroomName(fullName);
      if (!pendingForNewEventRef.current) onCoupleSet?.({ groom_name: fullName, bride_name: brideName ?? null, eventId });
    } else if (rel === 'bride') {
      const prevHolders = guestsRef.current.filter(g => g.role === 'bride' && g.name !== fullName);
      for (const prev of prevHolders) {
        if (prev.id) supabase.from('guests').update({ role: null, relationship: null }).eq('id', prev.id);
        const up = { ...prev, role: null, relationship: '' };
        guestsRef.current = guestsRef.current.map(g => g.id === prev.id ? up : g);
        setGuests(p => p.map(g => g.id === prev.id ? up : g));
      }
      setBrideName(fullName);
      if (!pendingForNewEventRef.current) onCoupleSet?.({ groom_name: groomName ?? null, bride_name: fullName, eventId });
    } else if (editingGuest) {
      // Role was removed from a guest who was previously groom or bride
      const newGroom = groomName === editingGuest.name ? null : groomName ?? null;
      const newBride = brideName === editingGuest.name ? null : brideName ?? null;
      if (groomName === editingGuest.name) setGroomName(null);
      if (brideName === editingGuest.name) setBrideName(null);
      onCoupleSet?.({ groom_name: newGroom, bride_name: newBride, eventId });
    }

    if (isCouple) { pendingForNewEventRef.current = false; onPendingRoleDone?.(); }

    setGuestForm({ salutation: 'none', firstName: '', middleName: '', lastName: '', category: 'mutual', relationship: '' });
    setGuestPhotoUrl(null);
    setGuestPhotoPanelOpen(false);
    setShowModal(false);
  };

  const handleRemoveAllGuests = async () => {
    const canvas = fabricCanvasRef.current;
    // Unseat every seated guest and restore seats
    seatTextObjectsRef.current.forEach(({ textObj, seatObj, groupObj }) => {
      canvas.remove(textObj);
      seatObj.set({ fill: '#e0e0e0', stroke: '#666', strokeWidth: 1 });
      groupObj.dirty = true;
    });
    seatTextObjectsRef.current = new Map();
    seatedGuestsRef.current = new Map();
    setSeatedGuests(new Map());
    // Clear groups
    guestGroupsRef.current = new Map();
    setGuestGroups(new Map());
    setSelectedGuests([]);
    setSelectMode(false);
    // Remove all guests from state and DB
    setGuests([]);
    guestsRef.current = [];
    canvas.requestRenderAll();
    setShowRemoveAllGuestsModal(false);
    await supabase.from('guests').delete().eq('event_id', eventId);
  };

  const handleRemoveGuests = async () => {
    const canvas = fabricCanvasRef.current;
    const toRemove = selectedGuests.map(i => guests[i]);
    const toRemoveNames = toRemove.map(g => g.name);
    const ids = toRemove.map(g => g.id).filter(Boolean);
    if (ids.length > 0) {
      await supabase.from('guests').delete().in('id', ids);
    }
    toRemoveNames.forEach(name => {
      // Unseat if seated
      const seatId = [...seatedGuestsRef.current.entries()].find(([, v]) => v === name)?.[0];
      if (seatId) {
        const entry = seatTextObjectsRef.current.get(seatId);
        if (entry) {
          canvas.remove(entry.textObj);
          entry.seatObj.set({ fill: '#e0e0e0', stroke: '#666', strokeWidth: 1 });
          entry.groupObj.dirty = true;
          seatTextObjectsRef.current.delete(seatId);
          seatedGuestsRef.current.delete(seatId);
        }
      }
    });
    // Remove groups that become empty after guest removal
    const affectedGroupIds = [];
    for (const [id, names] of guestGroupsRef.current) {
      if (names.some(n => toRemoveNames.includes(n))) affectedGroupIds.push(id);
    }
    if (affectedGroupIds.length > 0) {
      affectedGroupIds.forEach(id => supabase.from('guests').update({ group_id: null }).eq('group_id', id));
      setGuestGroups(prev => { const next = new Map(prev); affectedGroupIds.forEach(id => next.delete(id)); return next; });
      affectedGroupIds.forEach(id => guestGroupsRef.current.delete(id));
    }
    setGuests(prev => prev.filter((_, i) => !selectedGuests.includes(i)));
    setSeatedGuests(new Map(seatedGuestsRef.current));
    setSelectedGuests([]);
    if (canvas) canvas.renderAll();
  };

  const applyZoom = (newZoom) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    const clamped = Math.min(4, Math.max(0.2, newZoom));
    const center = { x: canvas.getWidth() / 2, y: canvas.getHeight() / 2 };
    canvas.zoomToPoint(center, clamped);
    zoomRef.current = clamped;
    setZoom(clamped);
  };

  const handleFitToScreen = () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    const tables = canvas.getObjects().filter(o => o.id?.startsWith('table-group-'));
    if (tables.length === 0) { applyZoom(1); return; }

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const obj of tables) {
      const c = obj.getCenterPoint();
      const hw = obj.getScaledWidth() / 2;
      const hh = obj.getScaledHeight() / 2;
      minX = Math.min(minX, c.x - hw);
      minY = Math.min(minY, c.y - hh);
      maxX = Math.max(maxX, c.x + hw);
      maxY = Math.max(maxY, c.y + hh);
    }

    const padding = 48;
    const canvasW = canvas.getWidth();
    const canvasH = canvas.getHeight();
    const newZoom = Math.min(4, Math.max(0.2,
      Math.min((canvasW - 2 * padding) / (maxX - minX), (canvasH - 2 * padding) / (maxY - minY))
    ));
    const panX = canvasW / 2 - ((minX + maxX) / 2) * newZoom;
    const panY = canvasH / 2 - ((minY + maxY) / 2) * newZoom;

    canvas.setViewportTransform([newZoom, 0, 0, newZoom, panX, panY]);
    zoomRef.current = newZoom;
    setZoom(newZoom);
    saveViewportRef.current?.();
    canvas.requestRenderAll();
  };

  const clearNavigationPath = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    if (navAnimRef.current) { cancelAnimationFrame(navAnimRef.current); navAnimRef.current = null; }
    for (const obj of navObjectsRef.current) canvas.remove(obj);
    navObjectsRef.current = [];
    canvas.requestRenderAll();
  }, []);

  const showNavigationPath = useCallback((guest) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    clearNavigationPath();

    let seatEntry = null;
    for (const [seatId, guestName] of seatedGuestsRef.current) {
      if (guestName === guest.name) { seatEntry = seatTextObjectsRef.current.get(seatId); break; }
    }
    if (!seatEntry) return;

    const { seatObj, groupObj } = seatEntry;
    const gc = groupObj.getCenterPoint();
    const scale = groupObj.scaleX;
    const destX = gc.x + seatObj.left * scale;
    const destY = gc.y + seatObj.top * scale;

    const door = canvas.getObjects().find(o => o.id === 'door-entrance');
    const dc = door ? door.getCenterPoint() : { x: canvas.getWidth() / 2, y: canvas.getHeight() - 20 };
    const startX = dc.x, startY = dc.y;

    // The seat lies INSIDE the obstacle-blocked zone (obstacle radius ~81, seat at ~55 from center).
    // Route A* to an approach point just OUTSIDE the obstacle, then append the actual seat.
    const obsRadius = (65 + NAV_OBS_PAD) * scale;
    const seatDx = destX - gc.x, seatDy = destY - gc.y;
    const seatDist = Math.sqrt(seatDx * seatDx + seatDy * seatDy) || 1;
    const approachX = gc.x + (seatDx / seatDist) * (obsRadius + NAV_CELL * 2);
    const approachY = gc.y + (seatDy / seatDist) * (obsRadius + NAV_CELL * 2);

    const routedPath = navBuildPath(canvas, startX, startY, approachX, approachY);
    const path = [...routedPath, { x: destX, y: destY }];

    // Pre-compute segments for animation
    const segments = [];
    let totalLen = 0;
    for (let i = 1; i < path.length; i++) {
      const dx = path[i].x - path[i-1].x, dy = path[i].y - path[i-1].y;
      const len = Math.sqrt(dx*dx + dy*dy);
      if (len < 0.001) continue;
      segments.push({ x0: path[i-1].x, y0: path[i-1].y, dx, dy, len, cumStart: totalLen });
      totalLen += len;
    }
    if (segments.length === 0) return;

    // Draw each path segment as an explicit Line (avoids Fabric Polyline coord issues)
    const pathLines = path.slice(1).map((pt, i) =>
      new fabric.Line([path[i].x, path[i].y, pt.x, pt.y], {
        stroke: '#FF6B35', strokeWidth: 2.5, strokeDashArray: [8, 5],
        selectable: false, evented: false,
      })
    );
    const pulse = new fabric.Circle({
      radius: 12, left: destX, top: destY,
      originX: 'center', originY: 'center',
      fill: 'rgba(255,107,53,0.25)', stroke: '#FF6B35', strokeWidth: 2,
      selectable: false, evented: false,
    });
    const arrow = new fabric.Triangle({
      width: 12, height: 16, left: startX, top: startY,
      originX: 'center', originY: 'center',
      fill: '#FF6B35', angle: 0, selectable: false, evented: false,
    });

    pathLines.forEach(l => canvas.add(l));
    canvas.add(pulse, arrow);
    navObjectsRef.current = [...pathLines, pulse, arrow];

    const speed = 120;
    let dist = 0, lastTime = null;
    const animate = (time) => {
      if (!lastTime) lastTime = time;
      dist = (dist + (time - lastTime) / 1000 * speed) % totalLen;
      lastTime = time;

      let seg = segments[segments.length - 1];
      for (const s of segments) { if (s.cumStart + s.len >= dist) { seg = s; break; } }
      const frac = Math.min(1, (dist - seg.cumStart) / seg.len);
      arrow.set({
        left: seg.x0 + seg.dx * frac,
        top:  seg.y0 + seg.dy * frac,
        angle: Math.atan2(seg.dy, seg.dx) * 180 / Math.PI + 90,
      });

      const p = (Math.sin(time / 400) + 1) / 2;
      pulse.set({ radius: 10 + 6 * p, opacity: 0.3 + 0.5 * (1 - p) });
      canvas.requestRenderAll();
      navAnimRef.current = requestAnimationFrame(animate);
    };
    navAnimRef.current = requestAnimationFrame(animate);
  }, [clearNavigationPath]);

  const isMac = /Mac|iPhone|iPad|iPod/.test(navigator.platform);

  const handleGuestSelection = (index, e) => {
    const name = guests[index]?.name;
    let groupMembersForSelection = null;
    for (const [, names] of guestGroupsRef.current) {
      if (names.includes(name)) { groupMembersForSelection = names; break; }
    }
    const groupIndices = groupMembersForSelection
      ? guests.map((g, i) => groupMembersForSelection.includes(g.name) ? i : -1).filter(i => i !== -1)
      : null;

    if (groupIndices) {
      // Clicking any grouped guest selects all group members
      setSelectedGuests(groupIndices);
      return;
    }

    const isMulti = selectMode || (e && (isMac ? e.metaKey : e.ctrlKey));
    if (isMulti) {
      setSelectedGuests(prev =>
        prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
      );
    } else {
      setSelectedGuests(prev =>
        prev.length === 1 && prev[0] === index ? [] : [index]
      );
    }
  };

  const handleSeatAll = () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const seatedNamesSet = new Set(seatedGuestsRef.current.values());
    const unseatedGuests = guests.map(g => g.name).filter(n => !seatedNamesSet.has(n));
    if (unseatedGuests.length === 0) return;

    // Build list of table groups with their available seats
    const tableSeatsMap = new Map(); // tableGroupObj → seat[]
    for (const obj of canvas.getObjects()) {
      if (obj.type !== 'group') continue;
      const seats = obj.getObjects().filter(c => c.id?.startsWith('seat-') && !seatedGuestsRef.current.has(c.id));
      if (seats.length > 0) tableSeatsMap.set(obj, seats);
    }

    // Collect groups that have unseated members
    const handledNames = new Set();
    const toSeatPairs = []; // { name, seatObj, groupObj }

    // First pass: seat each group together
    for (const [, groupMembers] of guestGroupsRef.current) {
      const unseated = groupMembers.filter(n => !seatedNamesSet.has(n) && unseatedGuests.includes(n));
      if (unseated.length === 0) continue;
      // Find a table with enough available seats
      for (const [tableObj, seats] of tableSeatsMap) {
        if (seats.length >= unseated.length) {
          unseated.forEach((name, i) => {
            toSeatPairs.push({ name, seatObj: seats[i], groupObj: tableObj });
            handledNames.add(name);
          });
          // Remove used seats from the map
          tableSeatsMap.set(tableObj, seats.slice(unseated.length));
          if (tableSeatsMap.get(tableObj).length === 0) tableSeatsMap.delete(tableObj);
          break;
        }
      }
      // If no table has enough seats, skip the group (they remain unseated)
    }

    // Second pass: seat remaining individual unseated guests
    const remainingGuests = unseatedGuests.filter(n => !handledNames.has(n));
    const remainingSeats = [];
    for (const [tableObj, seats] of tableSeatsMap) {
      seats.forEach(s => remainingSeats.push({ seatObj: s, groupObj: tableObj }));
    }
    // Shuffle remaining seats
    for (let i = remainingSeats.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [remainingSeats[i], remainingSeats[j]] = [remainingSeats[j], remainingSeats[i]];
    }
    remainingGuests.slice(0, remainingSeats.length).forEach((name, i) => {
      toSeatPairs.push({ name, seatObj: remainingSeats[i].seatObj, groupObj: remainingSeats[i].groupObj });
    });

    // Apply all seatings
    toSeatPairs.forEach(({ name, seatObj, groupObj }) => {
      seatObj.set({ fill: 'transparent', stroke: 'transparent' });
      groupObj.dirty = true;
      const gc = groupObj.getCenterPoint();
      const scale = groupObj.scaleX;
      const _sx = seatObj.left, _sy = seatObj.top;
      const _dist = Math.sqrt(_sx * _sx + _sy * _sy) || 1;
      const _lx = _sx + (_sx / _dist) * 10;
      const _ly = _sy + (_sy / _dist) * 10;
      const guestText = new fabric.FabricText(name, {
        left: gc.x + _lx * scale,
        top: gc.y + _ly * scale,
        fontSize: Math.max(5, 7 * scale),
        fill: '#222',
        originX: 'center', originY: 'center',
        selectable: true, hasControls: false, hasBorders: false,
        lockRotation: true, lockScalingX: true, lockScalingY: true,
        id: `guest-text-${seatObj.id}`, seatId: seatObj.id, guestName: name
      });
      canvas.add(guestText);
      seatTextObjectsRef.current.set(seatObj.id, { textObj: guestText, groupObj, seatObj });
      seatedGuestsRef.current.set(seatObj.id, name);
      const guestRecord = guestsRef.current.find(g => g.name === name);
      if (guestRecord?.id) {
        const tableNum = parseInt(groupObj.id.replace('table-group-', ''), 10);
        const seatObjs = groupObj.getObjects().filter(c => c.id?.startsWith('seat-'));
        const seatIdx = seatObjs.indexOf(seatObj);
        supabase.from('guests').update({ seat_id: seatObj.id, table_number: tableNum, seat_index: seatIdx }).eq('id', guestRecord.id)
          .then(({ error }) => { if (error) console.error('seatAll save failed:', error.message); });
      }
    });

    setSeatedGuests(new Map(seatedGuestsRef.current));
    canvas.renderAll();
  };

  const handleUnseatTableGuests = async (tableGroup) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !tableGroup) return;
    const seatedEntries = [...seatTextObjectsRef.current.entries()]
      .filter(([, entry]) => entry.groupObj === tableGroup);
    if (seatedEntries.length === 0) return;
    for (const [seatId, entry] of seatedEntries) {
      canvas.remove(entry.textObj);
      entry.seatObj.set({ fill: '#e0e0e0', stroke: '#666', strokeWidth: 1 });
      entry.groupObj.dirty = true;
      seatedGuestsRef.current.delete(seatId);
      seatTextObjectsRef.current.delete(seatId);
    }
    canvas.requestRenderAll();
    setSeatedGuests(new Map(seatedGuestsRef.current));
    for (const [, entry] of seatedEntries) {
      const guestName = entry.textObj.guestName;
      const guest = guestsRef.current.find(g => g.name === guestName);
      if (guest?.id) {
        supabase.from('guests').update({ seat_id: null, table_number: null, seat_index: null, arrived: false }).eq('id', guest.id)
          .then(({ error }) => { if (error) console.error('unseat failed:', error.message); });
        guestsRef.current = guestsRef.current.map(g => g.id === guest.id ? { ...g, arrived: false } : g);
        setGuests(prev => prev.map(g => g.id === guest.id ? { ...g, arrived: false } : g));
      }
    }
  };

  const handleDeleteTable = async () => {
    const canvas = fabricCanvasRef.current;
    const tableGroup = tableToDeleteRef.current;
    if (!canvas || !tableGroup) return;

    const tableNum = parseInt(tableGroup.id.replace('table-group-', ''), 10);

    // Collect all seated guests at this table before modifying refs
    const seatedEntries = [...seatTextObjectsRef.current.entries()]
      .filter(([, entry]) => entry.groupObj === tableGroup);

    // Remove text objects from canvas and clean up tracking refs
    for (const [seatId, entry] of seatedEntries) {
      canvas.remove(entry.textObj);
      seatedGuestsRef.current.delete(seatId);
      seatTextObjectsRef.current.delete(seatId);
    }

    // Remove the table group itself
    canvas.remove(tableGroup);
    canvas.requestRenderAll();

    setSeatedGuests(new Map(seatedGuestsRef.current));
    setTableCount(prev => prev - 1);
    setTotalSeats(prev => prev - 8);

    // Clear seat assignments in DB for guests that were at this table
    for (const [, entry] of seatedEntries) {
      const guestName = entry.textObj.guestName;
      const guest = guestsRef.current.find(g => g.name === guestName);
      if (guest?.id) {
        supabase.from('guests')
          .update({ seat_id: null, table_number: null, seat_index: null, arrived: false })
          .eq('id', guest.id)
          .then(({ error }) => { if (error) console.error('unseat on table delete failed:', error.message); });
        guestsRef.current = guestsRef.current.map(g => g.id === guest.id ? { ...g, arrived: false } : g);
        setGuests(prev => prev.map(g => g.id === guest.id ? { ...g, arrived: false } : g));
      }
    }

    // Delete the table from DB
    supabase.from('tables').delete().eq('table_number', tableNum)
      .then(({ error }) => { if (error) console.error('table delete failed:', error.message); });

    setShowDeleteTableModal(false);
    setTableContextMenu(null);
    tableToDeleteRef.current = null;
  };

  const handleRemoveAllTables = async () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    // Unseat all guests (remove text objects, clear seat state)
    seatTextObjectsRef.current.forEach(({ textObj, seatObj, groupObj }) => {
      canvas.remove(textObj);
      seatObj.set({ fill: '#e0e0e0', stroke: '#666', strokeWidth: 1 });
      groupObj.dirty = true;
    });
    seatTextObjectsRef.current = new Map();
    seatedGuestsRef.current = new Map();
    setSeatedGuests(new Map());
    // Remove all table groups from canvas
    const tableGroups = canvas.getObjects().filter(o => o.id?.startsWith('table-group-'));
    tableGroups.forEach(g => canvas.remove(g));
    canvas.requestRenderAll();
    setTableCount(0);
    setTotalSeats(0);
    setShowRemoveAllTablesModal(false);
    await supabase.from('guests').update({ seat_id: null, table_number: null, seat_index: null }).not('seat_id', 'is', null).eq('event_id', eventId);
    await supabase.from('tables').delete().eq('event_id', eventId);
  };

  const handleReset = async () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    seatTextObjectsRef.current.forEach(({ textObj, seatObj, groupObj }) => {
      canvas.remove(textObj);
      seatObj.set({ fill: '#e0e0e0', stroke: '#666', strokeWidth: 1 });
      groupObj.dirty = true;
    });
    seatTextObjectsRef.current = new Map();
    seatedGuestsRef.current = new Map();
    setSeatedGuests(new Map());
    canvas.renderAll();
    setShowResetModal(false);
    await supabase.from('guests').update({ seat_id: null, table_number: null, seat_index: null }).not('seat_id', 'is', null).eq('event_id', eventId);
  };

  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

  const buildCalendarDays = () => {
    const { month, year } = calNav;
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
  };

  const seatedCount   = seatedGuests.size;
  const unseatedCount = guests.length - seatedCount;
  const available     = totalSeats - seatedCount;
  const arrivedCount  = guests.filter(g => g.arrived).length;
  const notArrivedCount = seatedCount - arrivedCount;

  // Build a map of guestName -> tableNumber from the seatTextObjectsRef
  const guestTableMap = new Map();
  for (const [seatId, guestName] of seatedGuests.entries()) {
    const entry = seatTextObjectsRef.current.get(seatId);
    if (!entry) continue;
    const tableChild = entry.groupObj.getObjects().find(c => c.id && c.id.startsWith('table-'));
    if (tableChild) {
      const tableNumber = parseInt(tableChild.id.replace('table-', ''), 10);
      guestTableMap.set(guestName, tableNumber);
    }
  }

  const filteredGuests = (() => {
    const searchLower = guestSearch.trim().toLowerCase();
    const list = guests.filter(g => {
      if (guestFilter === 'unseated' && Array.from(seatedGuests.values()).includes(g.name)) return false;
      if (guestFilter !== 'all' && guestFilter !== 'unseated' && g.category !== guestFilter) return false;
      if (searchLower && !g.name.toLowerCase().includes(searchLower)) return false;
      return true;
    });
    if (tableSortDir) {
      return [...list].sort((a, b) => {
        const tA = guestTableMap.get(a.name) ?? Infinity;
        const tB = guestTableMap.get(b.name) ?? Infinity;
        return tableSortDir === 'asc' ? tA - tB : tB - tA;
      });
    }
    if (nameSortDir) {
      return [...list].sort((a, b) => {
        const lastName = name => name.trim().split(/\s+/).pop().toLowerCase();
        const cmp = lastName(a.name).localeCompare(lastName(b.name));
        return nameSortDir === 'asc' ? cmp : -cmp;
      });
    }
    if (groupSortDir) {
      return [...list].sort((a, b) => {
        const isGrouped = (name) => { for (const [, names] of guestGroups) { if (names.includes(name)) return true; } return false; };
        const gA = isGrouped(a.name) ? 1 : 0;
        const gB = isGrouped(b.name) ? 1 : 0;
        return groupSortDir === 'asc' ? gB - gA : gA - gB;
      });
    }
    return list;
  })();

  return (
    <div>
      <div className="reception-container">
        <div className="canvas-column">
          <div className="canvas-header-row">
            <div className="canvas-stats">
              <span>Seated: {seatedCount}</span>
              <span>Unseated: {unseatedCount}</span>
              <span>Available: {available}</span>
              <span>Arrived: {arrivedCount}</span>
              <span>Not Arrived: {notArrivedCount}</span>
            </div>
            <div className="venue-container">
              {venueName && (
                <a
                  className="venue-link"
                  href={`https://maps.apple.com/?q=${encodeURIComponent(venueAddress)}`}
                  target="_blank"
                  rel="noreferrer"
                  title={venueAddress}
                >{venueName}</a>
              )}
              {venueName && venueAddress && (
                <a
                  className="venue-address-link"
                  href={`https://maps.apple.com/?q=${encodeURIComponent(venueAddress)}`}
                  target="_blank"
                  rel="noreferrer"
                >{venueAddress}</a>
              )}
              <button className="set-venue-button" onClick={() => { setVenueForm({ name: venueName, address: venueName ? venueAddress : '' }); setShowVenueModal(true); }}>set venue</button>
            </div>
            {displayDate && (() => {
              const days = Math.ceil((new Date(displayDate.getFullYear(), displayDate.getMonth(), displayDate.getDate()) - new Date(today.getFullYear(), today.getMonth(), today.getDate())) / 86400000);
              if (days < 0) return null;
              return (
                <span className="days-until">
                  {days === 0 ? 'Today is the big day!' : `${days} days remaining`}
                </span>
              );
            })()}
          </div>
          <canvas ref={canvasRef} />
          <div className="canvas-controls">
            <button onClick={addGuestTable} className="add-table-button" disabled={tableCount >= 50}>Add Table</button>
            <div className="custom-select-wrapper" ref={selectRef}>
              <button className="custom-select-trigger" onClick={() => setSelectOpen(o => !o)} title="Number of tables to add">
                {tablesToAdd} ▾
              </button>
              {selectOpen && (
                <ul className="custom-select-list">
                  {Array.from({ length: Math.max(0, 50 - tableCount) }, (_, i) => i + 1).map(n => (
                    <li
                      key={n}
                      className={`custom-select-option${n === tablesToAdd ? ' selected' : ''}`}
                      onClick={() => { setTablesToAdd(n); setSelectOpen(false); }}
                    >
                      {n}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <span className="table-count-label">Total Number of Tables: {tableCount}</span>
            <button className="remove-all-tables-button" onClick={() => setShowRemoveAllTablesModal(true)} disabled={tableCount === 0} title="Unseat guests and remove all tables">Remove All Tables</button>
            <div className="canvas-zoom-controls">
              <button className="zoom-button" onClick={() => applyZoom(zoomRef.current / 1.2)} title="Zoom Out">−</button>
              <span className="zoom-label">{Math.round(zoom * 100)}%</span>
              <button className="zoom-button" onClick={() => applyZoom(zoomRef.current * 1.2)} title="Zoom In">+</button>
              <button className="zoom-button zoom-reset" onClick={handleFitToScreen} title="Fit to screen">⊙</button>
            </div>
            <button onClick={handleSeatAll} className="seat-all-button" style={{marginLeft: 'auto'}} disabled={unseatedCount === 0} title="Seat unseated guests">Seat All</button>
            <button onClick={() => setShowResetModal(true)} className="reset-button" disabled={seatedCount === 0} title="Unseat all guests">Clear All</button>
          </div>
        </div>
        <div className="guest-list" onMouseLeave={clearNavigationPath}>
          <div className="guest-list-title">Guest List</div>
          <div className="guest-list-filter-row">
            <select
              title="Guest Category"
              className="guest-filter-select"
              value={guestFilter}
              onChange={e => { setGuestFilter(e.target.value); setSelectedGuests([]); }}
            >
              <option value="all">All Guests</option>
              <option value="groom">Groom's Guests</option>
              <option value="bride">Bride's Guests</option>
              <option value="mutual">Mutual</option>
              <option value="unseated">Unseated</option>
            </select>
            <button
              className={`select-mode-button${selectMode ? ' select-mode-button--active' : ''}`}
              onClick={() => { setSelectMode(m => !m); if (selectMode) setSelectedGuests([]); }}
              title="Select multiple guests"
            >Select</button>
          </div>
          <div className="guest-list-header">
            <span
              className="guest-list-col-table guest-list-col-table--sortable"
              onClick={() => { setTableSortDir(d => d === 'asc' ? 'desc' : 'asc'); setNameSortDir(null); }}
            ># {tableSortDir === 'asc' ? '▲' : tableSortDir === 'desc' ? '▼' : '⇅'}</span>
            <span
              className="guest-list-col-group guest-list-col-group--sortable"
              title="Sort by grouped"
              onClick={() => { setGroupSortDir(d => d === 'asc' ? 'desc' : 'asc'); setTableSortDir(null); setNameSortDir(null); }}
            >&{groupSortDir === 'asc' ? ' ▲' : groupSortDir === 'desc' ? ' ▼' : ''}</span>
            <span
              className="guest-list-col-name"
              onClick={() => { setNameSortDir(d => d === 'asc' ? 'desc' : 'asc'); setTableSortDir(null); }}
            >
              Name {nameSortDir === 'asc' ? '▲' : nameSortDir === 'desc' ? '▼' : '⇅'}
            </span>
          </div>
          <div className="guest-list-box" onClick={(e) => { if (e.target === e.currentTarget) setSelectedGuests([]); }}>
            {filteredGuests.map((guest) => {
              const index = guests.indexOf(guest);
              const isSeated = Array.from(seatedGuests.values()).includes(guest.name);
              return (
                <div
                  key={index}
                  className={`guest-item${selectedGuests.includes(index) ? ' selected' : ''}${isSeated ? ' seated' : ''}${guest.name === groomName ? ' guest-item--groom' : ''}${guest.name === brideName ? ' guest-item--bride' : ''}${guest.arrived ? ' guest-item--arrived' : ''}`}
                  draggable={!isSeated}
                  onDragStart={(e) => {
                    if (isSeated) { e.preventDefault(); return; }
                    clearTimeout(guestLongPressTimerRef.current);
                    guestLongPressTimerRef.current = null;
                    let dragIndices;
                    let groupMembers = null;
                    for (const [, names] of guestGroupsRef.current) {
                      if (names.includes(guest.name)) { groupMembers = names; break; }
                    }
                    const groupIndices = groupMembers
                      ? guests.map((g, i) => groupMembers.includes(g.name) ? i : -1).filter(i => i !== -1)
                      : null;
                    if (groupIndices) {
                      dragIndices = groupIndices.filter(i => !Array.from(seatedGuests.values()).includes(guests[i].name));
                    } else if (selectedGuests.includes(index) && selectedGuests.length > 1) {
                      dragIndices = selectedGuests.filter(i => !Array.from(seatedGuests.values()).includes(guests[i].name));
                    } else {
                      dragIndices = [index];
                    }
                    const names = dragIndices.map(i => guests[i].name);
                    e.dataTransfer.setData('text/plain', JSON.stringify(names));
                    e.dataTransfer.effectAllowed = 'copy';
                  }}
                  onClick={(e) => isSeated ? showNavigationPath(guest) : handleGuestSelection(index, e)}
                  onDoubleClick={() => { if (!isSeated) openEditGuest(guest); }}
                  onMouseDown={(e) => {
                    guestLongPressPosRef.current = { x: e.clientX, y: e.clientY };
                    guestLongPressTimerRef.current = setTimeout(() => {
                      guestLongPressTimerRef.current = null;
                      guestMenuJustOpenedRef.current = true;
                      setGuestContextMenu({ x: e.clientX, y: e.clientY, guest, isSeated });
                    }, 600);
                  }}
                  onMouseMove={(e) => {
                    if (guestLongPressTimerRef.current) {
                      const dx = e.clientX - guestLongPressPosRef.current.x;
                      const dy = e.clientY - guestLongPressPosRef.current.y;
                      if (Math.sqrt(dx * dx + dy * dy) > 5) {
                        clearTimeout(guestLongPressTimerRef.current);
                        guestLongPressTimerRef.current = null;
                      }
                    }
                    if (guest.photo) setPhotoTooltip(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : prev);
                  }}
                  onMouseUp={() => { clearTimeout(guestLongPressTimerRef.current); guestLongPressTimerRef.current = null; }}
                  onMouseEnter={guest.photo ? (e) => setPhotoTooltip({ url: guest.photo, name: guest.name, x: e.clientX, y: e.clientY }) : undefined}
                  onMouseLeave={(e) => { clearTimeout(guestLongPressTimerRef.current); guestLongPressTimerRef.current = null; if (guest.photo) setPhotoTooltip(null); }}
                  onTouchStart={(e) => {
                    const touch = e.touches[0];
                    guestLongPressPosRef.current = { x: touch.clientX, y: touch.clientY };
                    guestLongPressTimerRef.current = setTimeout(() => {
                      guestLongPressTimerRef.current = null;
                      guestMenuJustOpenedRef.current = true;
                      setGuestContextMenu({ x: touch.clientX, y: touch.clientY, guest, isSeated });
                    }, 600);
                  }}
                  onTouchMove={(e) => {
                    if (guestLongPressTimerRef.current) {
                      const touch = e.touches[0];
                      const dx = touch.clientX - guestLongPressPosRef.current.x;
                      const dy = touch.clientY - guestLongPressPosRef.current.y;
                      if (Math.sqrt(dx * dx + dy * dy) > 10) {
                        clearTimeout(guestLongPressTimerRef.current);
                        guestLongPressTimerRef.current = null;
                      }
                    }
                  }}
                  onTouchEnd={() => { clearTimeout(guestLongPressTimerRef.current); guestLongPressTimerRef.current = null; }}
                  onTouchCancel={() => { clearTimeout(guestLongPressTimerRef.current); guestLongPressTimerRef.current = null; }}
                  onContextMenu={(e) => e.preventDefault()}
                >
                  <span className="guest-item-table">{guestTableMap.get(guest.name) ?? ''}</span>
                  <span className="guest-item-group">{[...guestGroups.values()].some(names => names.includes(guest.name)) ? '&' : ''}</span>
                  <span className="guest-item-name">{guest.name}</span>
                </div>
              );
            })}
          </div>
          <div className="guest-search-row">
            <input
              className="guest-search-input"
              type="text"
              placeholder="Search guests…"
              value={guestSearch}
              onChange={e => setGuestSearch(e.target.value)}
            />
            {guestSearch && (
              <button className="guest-search-clear" onClick={() => setGuestSearch('')} title="Clear search">×</button>
            )}
          </div>
          <div className="guest-list-footer">
            <div className="guest-list-buttons">
              <button
                className="remove-all-guests-button"
                title="Remove All Guests"
                disabled={guests.length === 0}
                onClick={() => setShowRemoveAllGuestsModal(true)}
              >Remove All</button>
              {(() => {
                const selectedNames = selectedGuests.map(i => guests[i]?.name).filter(Boolean);
                const ungroupableIds = [];
                for (const [id, names] of guestGroups) {
                  if (!names.some(n => selectedNames.includes(n))) continue;
                  const isCouple = groomName && brideName && names.includes(groomName) && names.includes(brideName);
                  if (!isCouple) ungroupableIds.push(id);
                }
                const isUngroup = ungroupableIds.length > 0;
                const isGroup = !isUngroup && selectedGuests.length >= 2;
                const isDisabled = !isUngroup && !isGroup;
                return (
                  <button
                    className="group-toggle-button"
                    disabled={isDisabled}
                    title={isUngroup ? 'Ungroup' : 'Group'}
                    onClick={async () => {
                      if (isUngroup) {
                        for (const id of ungroupableIds) {
                          await supabase.from('guests').update({ group_id: null }).eq('group_id', id);
                        }
                        setGuestGroups(prev => { const next = new Map(prev); ungroupableIds.forEach(id => next.delete(id)); return next; });
                        ungroupableIds.forEach(id => guestGroupsRef.current.delete(id));
                      } else {
                        const names = selectedGuests.map(i => guests[i].name);
                        const newGroupId = randomUUID();
                        const ids = selectedGuests.map(i => guests[i].id).filter(Boolean);
                        if (ids.length > 0) await supabase.from('guests').update({ group_id: newGroupId }).in('id', ids);
                        setGuestGroups(prev => new Map(prev).set(newGroupId, names));
                        guestGroupsRef.current.set(newGroupId, names);
                      }
                      setSelectMode(false);
                      setSelectedGuests([]);
                    }}
                  >{isUngroup ? 'Ungroup' : 'Group'}</button>
                );
              })()}
              <button onClick={() => {
                const category = (guestFilter === 'all' || guestFilter === 'unseated') ? 'mutual' : guestFilter;
                setGuestForm(f => ({ ...f, category }));
                setShowModal(true);
              }} className="add-guest-button" title="Add Guest">+</button>
              <button onClick={handleRemoveGuests} className="remove-guest-button" title="Remove Guest" disabled={selectedGuests.length === 0}>-</button>
            </div>
            <span className="guest-count">Number of Guests: {filteredGuests.length}</span>
          </div>
        </div>
      </div>

      {calendarOpen && (
        <div className="modal-overlay">
          <div className="modal-content calendar-modal" ref={calendarRef}>
            <div className="cal-nav">
              <button onClick={() => setCalNav(n => {
                const d = new Date(n.year, n.month - 1, 1);
                return { month: d.getMonth(), year: d.getFullYear() };
              })}>‹</button>
              <span>{MONTHS[calNav.month]} {calNav.year}</span>
              <button onClick={() => setCalNav(n => {
                const d = new Date(n.year, n.month + 1, 1);
                return { month: d.getMonth(), year: d.getFullYear() };
              })}>›</button>
            </div>
            <div className="cal-grid">
              {DAYS.map(d => <div key={d} className="cal-day-header">{d}</div>)}
              {buildCalendarDays().map((day, i) => {
                const isSelected = day &&
                  pendingDate.getDate() === day &&
                  pendingDate.getMonth() === calNav.month &&
                  pendingDate.getFullYear() === calNav.year;
                return (
                  <div
                    key={i}
                    className={`cal-day${day ? ' cal-day-active' : ''}${isSelected ? ' cal-day-selected' : ''}`}
                    onClick={() => day && setPendingDate(new Date(calNav.year, calNav.month, day))}
                  >
                    {day || ''}
                  </div>
                );
              })}
            </div>
            <div className="modal-buttons">
              <button type="button" onClick={() => setCalendarOpen(false)}>Cancel</button>
              <button type="button" onClick={() => {
                setDisplayDate(pendingDate);
                setCalendarOpen(false);
                const iso = `${pendingDate.getFullYear()}-${String(pendingDate.getMonth()+1).padStart(2,'0')}-${String(pendingDate.getDate()).padStart(2,'0')}`;
                onDateSet?.({ iso, eventId });
              }}>Ok</button>
            </div>
          </div>
        </div>
      )}

      {showVenueModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Set Venue</h2>
            <div className="form-group" style={{ position: 'relative' }}>
              <label>Venue Name:</label>
              <input
                type="text"
                value={venueForm.name}
                onChange={e => { setVenueForm(f => ({ ...f, name: e.target.value })); setVenueResults([]); }}
              />
              {venueAddressLoading && <span className="venue-loading"> looking up…</span>}
              {venueResults.length > 0 && (
                <ul className="venue-results-list">
                  {venueResults.map((r, i) => (
                    <li
                      key={i}
                      className="venue-results-item"
                      onClick={() => {
                        setVenueForm(f => ({ ...f, name: r.displayName, address: r.address }));
                        setVenueResults([]);
                      }}
                    >
                      <span className="venue-results-name">{r.displayName}</span>
                      <span className="venue-results-subtitle">{r.subtitle}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="form-group">
              <label>Address:</label>
              <input
                type="text"
                value={venueForm.address}
                onChange={e => setVenueForm(f => ({ ...f, address: e.target.value }))}
              />
            </div>
            <div className="modal-buttons">
              <button type="button" onClick={() => {
                setShowVenueModal(false);
                setVenueResults([]);
                if (venueModalForEventRef.current) { venueModalForEventRef.current = false; onVenueForEventSelected?.(null); }
              }}>Cancel</button>
              <button
                type="button"
                onClick={() => {
                  if (venueForm.name.trim()) {
                    const name = venueForm.name.trim().replace(/\b\w/g, c => c.toUpperCase());
                    const addr = venueForm.address.trim();
                    setVenueName(name);
                    setVenueAddress(addr);
                    setShowVenueModal(false);
                    setVenueResults([]);
                    supabase.from('venues').upsert({ name, address: addr || null }, { onConflict: 'name' })
                      .then(({ error }) => { if (error) console.error('venue save failed:', error.message); });
                    supabase.from('canvas_state').upsert({ id: eventId, venue_name: name, venue_address: addr || null })
                      .then(({ error }) => { if (error) console.error('venue state save failed:', error.message); });
                    onVenueSet?.({ name, eventId });
                    if (venueModalForEventRef.current) { venueModalForEventRef.current = false; onVenueForEventSelected?.(name); }
                  }
                }}
              >Ok</button>
            </div>
          </div>
        </div>
      )}

      {showResetModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Unseat All Guests?</h2>
            <p>All guests will be removed from their seats and returned to the guest list. Tables will remain.</p>
            <div className="modal-buttons">
              <button type="button" onClick={() => setShowResetModal(false)}>Cancel</button>
              <button type="button" className="reset-confirm-button" onClick={handleReset}>Yes, Unseat All</button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="add-guest-modal-wrapper">
            <div className="modal-content" style={{ position: 'relative' }}>
              <h2>{editingGuest ? 'Edit Guest' : 'Add New Guest'}</h2>
              <form onSubmit={handleAddGuest}>
                <div className="salutation-group">
                  <label>
                    <input
                      type="radio"
                      name="salutation"
                      value="none"
                      checked={guestForm.salutation === 'none'}
                      onChange={handleGuestFormChange}
                    /> None
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="salutation"
                      value="mr"
                      checked={guestForm.salutation === 'mr'}
                      onChange={handleGuestFormChange}
                    /> Mr
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="salutation"
                      value="mrs"
                      checked={guestForm.salutation === 'mrs'}
                      onChange={handleGuestFormChange}
                    /> Mrs
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="salutation"
                      value="ms"
                      checked={guestForm.salutation === 'ms'}
                      onChange={handleGuestFormChange}
                    /> Ms
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="salutation"
                      value="miss"
                      checked={guestForm.salutation === 'miss'}
                      onChange={handleGuestFormChange}
                    /> Miss
                  </label>
                </div>

                <div className="form-group">
                  <label>First Name:</label>
                  <input
                    type="text"
                    name="firstName"
                    value={guestForm.firstName}
                    onChange={handleGuestFormChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Middle Name:</label>
                  <input
                    type="text"
                    name="middleName"
                    value={guestForm.middleName}
                    onChange={handleGuestFormChange}
                  />
                </div>

                <div className="form-group">
                  <label>Last Name:</label>
                  <input
                    type="text"
                    name="lastName"
                    value={guestForm.lastName}
                    onChange={handleGuestFormChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Guest Category:</label>
                  <select name="category" value={guestForm.category} onChange={handleGuestFormChange} className="guest-category-select">
                    <option value="groom">Groom's Guest</option>
                    <option value="bride">Bride's Guest</option>
                    <option value="mutual">Mutual</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Relationship:</label>
                  <select name="relationship" value={guestForm.relationship} onChange={handleGuestFormChange} className="guest-category-select">
                    {RELATIONSHIP_OPTIONS.map(opt => {
                      const editingName = editingGuest?.name;
                      const groomTaken = opt.value === 'groom' && groomName && groomName !== editingName;
                      const brideTaken = opt.value === 'bride' && brideName && brideName !== editingName;
                      return (
                        <option key={opt.value} value={opt.value} disabled={groomTaken || brideTaken}>
                          {opt.label}{groomTaken || brideTaken ? ' (taken)' : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="modal-buttons">
                  <button type="button" onClick={() => { pendingForNewEventRef.current = false; setShowModal(false); setGuestPhotoUrl(null); setGuestPhotoPanelOpen(false); resetCropState(); setEditingGuest(null); onPendingRoleDone?.(); }}>
                    Cancel
                  </button>
                  <button type="submit">
                    {editingGuest ? 'Update' : 'Add'}
                  </button>
                </div>
              </form>

              {/* Side tab */}
              <button
                className={`guest-photo-tab${guestPhotoPanelOpen ? ' guest-photo-tab--open' : ''}`}
                onClick={() => setGuestPhotoPanelOpen(o => !o)}
                title="Guest Photo"
              >&#9654;</button>
            </div>

            {/* Slide-out photo panel */}
            <div className={`guest-photo-panel${guestPhotoPanelOpen ? ' guest-photo-panel--open' : ''}`}>
              {guestPhotoUrl ? (
                <div className="crop-editor">
                  <canvas
                    ref={cropCanvasRef}
                    width={120}
                    height={120}
                    className="crop-canvas"
                    onPointerDown={onCropPointerDown}
                    onPointerMove={onCropPointerMove}
                    onPointerUp={onCropPointerUp}
                  />
                  <div className="crop-zoom-controls">
                    <button type="button" className="crop-zoom-btn" onClick={() => setCropZoomValue(Math.max(1, cropZoom - 0.1))}>−</button>
                    <input
                      type="range" min="1" max="5" step="0.05"
                      value={cropZoom}
                      className="crop-zoom-slider"
                      onChange={e => setCropZoomValue(parseFloat(e.target.value))}
                    />
                    <button type="button" className="crop-zoom-btn" onClick={() => setCropZoomValue(Math.min(5, cropZoom + 0.1))}>+</button>
                  </div>
                </div>
              ) : (
                <div className="guest-photo-preview">
                  <span className="guest-photo-placeholder">No image</span>
                </div>
              )}
              <button
                type="button"
                className="select-image-button"
                onClick={() => photoFileInputRef.current && photoFileInputRef.current.click()}
              >Select Image</button>
              <input
                ref={photoFileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif"
                style={{ display: 'none' }}
                onChange={e => {
                  const file = e.target.files[0];
                  if (!file) return;
                  photoFileRef.current = file;
                  const blobUrl = URL.createObjectURL(file);
                  const img = new Image();
                  img.onload = () => {
                    cropImageRef.current = img;
                    cropZoomRef.current = 1;
                    cropOffsetRef.current = { x: 0, y: 0 };
                    setCropZoom(1);
                    setGuestPhotoUrl(blobUrl);
                  };
                  img.onerror = () => URL.revokeObjectURL(blobUrl);
                  img.src = blobUrl;
                  e.target.value = '';
                }}
              />
            </div>
          </div>
        </div>
      )}

      {photoTooltip && (
        <div
          className="guest-photo-tooltip"
          style={{ left: photoTooltip.x + 14, top: photoTooltip.y + 14 }}
        >
          <img
            src={photoTooltip.url}
            alt={photoTooltip.name ?? ''}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      )}

      {guestContextMenu && (
        <>
          <div className="table-context-backdrop" onClick={() => {
            if (guestMenuJustOpenedRef.current) { guestMenuJustOpenedRef.current = false; return; }
            setGuestContextMenu(null);
          }} />
          <div className="table-context-menu" style={{ left: guestContextMenu.x, top: guestContextMenu.y }}>
            <button className="table-context-menu-item" onClick={() => { openEditGuest(guestContextMenu.guest); setGuestContextMenu(null); }}>
              Edit Profile
            </button>
            {guestContextMenu.isSeated && !guestContextMenu.guest.arrived && (
              <button className="table-context-menu-item" onClick={async () => {
                const guest = guestContextMenu.guest;
                let affectedNames = [guest.name];
                for (const [, names] of guestGroupsRef.current) {
                  if (names.includes(guest.name)) { affectedNames = names; break; }
                }
                const seatedNames = new Set(seatedGuestsRef.current.values());
                const toUpdate = guestsRef.current.filter(g => affectedNames.includes(g.name) && seatedNames.has(g.name) && g.id);
                if (toUpdate.length > 0) await supabase.from('guests').update({ arrived: true }).in('id', toUpdate.map(g => g.id));
                guestsRef.current = guestsRef.current.map(g => affectedNames.includes(g.name) && seatedNames.has(g.name) ? { ...g, arrived: true } : g);
                setGuests(prev => prev.map(g => affectedNames.includes(g.name) && seatedNames.has(g.name) ? { ...g, arrived: true } : g));
                syncAllTableColors();
                setGuestContextMenu(null);
              }}>Arrived</button>
            )}
            {guestContextMenu.isSeated && guestContextMenu.guest.arrived && (
              <button className="table-context-menu-item" onClick={async () => {
                const guest = guestContextMenu.guest;
                let affectedNames = [guest.name];
                for (const [, names] of guestGroupsRef.current) {
                  if (names.includes(guest.name)) { affectedNames = names; break; }
                }
                const toUpdate = guestsRef.current.filter(g => affectedNames.includes(g.name) && g.id);
                if (toUpdate.length > 0) await supabase.from('guests').update({ arrived: false }).in('id', toUpdate.map(g => g.id));
                guestsRef.current = guestsRef.current.map(g => affectedNames.includes(g.name) ? { ...g, arrived: false } : g);
                setGuests(prev => prev.map(g => affectedNames.includes(g.name) ? { ...g, arrived: false } : g));
                syncAllTableColors();
                setGuestContextMenu(null);
              }}>Not Arrived</button>
            )}
            <button className="table-context-menu-item" onClick={async () => {
              const guest = guestContextMenu.guest;
              const isGroom = groomName === guest.name;
              const newRel = isGroom ? '' : 'groom';
              if (guest.id) await supabase.from('guests').update({ role: isGroom ? null : 'groom', relationship: isGroom ? null : 'groom' }).eq('id', guest.id);
              if (!isGroom) {
                const prevHolders = guestsRef.current.filter(g => g.role === 'groom' && g.id !== guest.id);
                for (const prev of prevHolders) {
                  if (prev.id) await supabase.from('guests').update({ role: null, relationship: null }).eq('id', prev.id);
                  const up = { ...prev, role: null, relationship: '' };
                  guestsRef.current = guestsRef.current.map(g => g.id === prev.id ? up : g);
                  setGuests(p => p.map(g => g.id === prev.id ? up : g));
                }
              }
              const updatedGuest = { ...guest, role: isGroom ? null : 'groom', relationship: newRel };
              guestsRef.current = guestsRef.current.map(g => g.id === guest.id ? updatedGuest : g);
              setGuests(prev2 => prev2.map(g => g.id === guest.id ? updatedGuest : g));
              setGroomName(isGroom ? null : guest.name);
              onCoupleSet?.({ groom_name: isGroom ? null : guest.name, bride_name: brideName ?? null, eventId });
              setGuestContextMenu(null);
            }}>
              {groomName === guestContextMenu.guest.name ? 'Unset Groom' : 'Set as Groom'}
            </button>
            <button className="table-context-menu-item" onClick={async () => {
              const guest = guestContextMenu.guest;
              const isBride = brideName === guest.name;
              const newRel = isBride ? '' : 'bride';
              if (guest.id) await supabase.from('guests').update({ role: isBride ? null : 'bride', relationship: isBride ? null : 'bride' }).eq('id', guest.id);
              if (!isBride) {
                const prevHolders = guestsRef.current.filter(g => g.role === 'bride' && g.id !== guest.id);
                for (const prev of prevHolders) {
                  if (prev.id) await supabase.from('guests').update({ role: null, relationship: null }).eq('id', prev.id);
                  const up = { ...prev, role: null, relationship: '' };
                  guestsRef.current = guestsRef.current.map(g => g.id === prev.id ? up : g);
                  setGuests(p => p.map(g => g.id === prev.id ? up : g));
                }
              }
              const updatedGuest = { ...guest, role: isBride ? null : 'bride', relationship: newRel };
              guestsRef.current = guestsRef.current.map(g => g.id === guest.id ? updatedGuest : g);
              setGuests(prev2 => prev2.map(g => g.id === guest.id ? updatedGuest : g));
              setBrideName(isBride ? null : guest.name);
              onCoupleSet?.({ groom_name: groomName ?? null, bride_name: isBride ? null : guest.name, eventId });
              setGuestContextMenu(null);
            }}>
              {brideName === guestContextMenu.guest.name ? 'Unset Bride' : 'Set as Bride'}
            </button>
            <button className="table-context-menu-item" onClick={() => setGuestContextMenu(null)}>
              Cancel
            </button>
          </div>
        </>
      )}
      {tableContextMenu && (
        <>
          <div className="table-context-backdrop" onClick={() => setTableContextMenu(null)} />
          <div
            className="table-context-menu"
            style={{ left: tableContextMenu.x, top: tableContextMenu.y }}
          >
            <button
              className="table-context-menu-item"
              onClick={() => {
                const tg = tableContextMenu.tableGroup;
                setTableContextMenu(null);
                handleUnseatTableGuests(tg);
              }}
            >
              Unseat All Guests
            </button>
            <button
              className="table-context-menu-item table-context-menu-item--danger"
              onClick={() => {
                tableToDeleteRef.current = tableContextMenu.tableGroup;
                setTableContextMenu(null);
                setShowDeleteTableModal(true);
              }}
            >
              Delete Table
            </button>
            <button
              className="table-context-menu-item"
              onClick={() => setTableContextMenu(null)}
            >
              Cancel
            </button>
          </div>
        </>
      )}

      {showRemoveAllGuestsModal && (
        <div className="modal-overlay" onClick={() => setShowRemoveAllGuestsModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Remove All Guests?</h2>
            <p>
              All guests will be permanently removed from the guest list. Seated guests will
              be unseated and their table assignments cleared. This cannot be undone.
            </p>
            <div className="modal-buttons">
              <button className="confirm-button" onClick={handleRemoveAllGuests}>Yes, Remove All</button>
              <button className="cancel-button" autoFocus onClick={() => setShowRemoveAllGuestsModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {showRemoveAllTablesModal && (
        <div className="modal-overlay" onClick={() => setShowRemoveAllTablesModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Remove All Tables?</h2>
            <p>
              All tables will be removed from the canvas. Any seated guests will be returned
              to the guest list as unseated. This cannot be undone.
            </p>
            <div className="modal-buttons">
              <button className="confirm-button" onClick={handleRemoveAllTables}>Yes, Remove All</button>
              <button className="cancel-button" autoFocus onClick={() => setShowRemoveAllTablesModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {showDeleteTableModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteTableModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Delete Table?</h2>
            <p>
              Any guests seated at this table will be returned to the guest list as unseated.
              This cannot be undone.
            </p>
            <div className="modal-buttons">
              <button className="confirm-button" onClick={handleDeleteTable}>Yes, Delete</button>
              <button className="cancel-button" autoFocus onClick={() => setShowDeleteTableModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Reception;
